import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from 'src/app/services/user/user.service';
import { PlanService } from '../../plan.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import * as alertifyjs from 'alertifyjs';
import * as XLSX from 'xlsx';
import { PaymentTerm } from '../shared/payment-terms-builder/payment-terms-builder.component';

@Component({
  selector: 'app-plans-wizard',
  templateUrl: './plans-wizard.component.html',
  styleUrls: ['./plans-wizard.component.css']
})
export class PlansWizardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Control del wizard
  currentStep = 1;
  totalSteps = 8;
  completedSteps: number[] = [];

  // CONFIGURACIÓN DE NAVEGACIÓN
  /**
   * Control de navegación del wizard
   *
   * true = Navegación secuencial obligatoria (producción)
   * false = Navegación libre para pruebas (desarrollo)
   */
  enforceSequentialNavigation = true; // Navegación secuencial por defecto

  // Formularios por paso
  step1Form!: FormGroup; // Etiquetas
  step2Form!: FormGroup; // Penalidades/Fees
  step3Form!: FormGroup; // Productos
  step4Form!: FormGroup; // Datos Básicos
  step5Form!: FormGroup; // Datos Generales
  step6Form!: FormGroup; // Filtros
  step7Form!: FormGroup; // Otros
  step8Form!: FormGroup; // Finalizar

  // Estados de carga
  isLoading = false;
  isSaving = false;
  isCheckingCode = false;
  codeCheckResult: any = null;

  // Datos para Paso 1 - Etiquetas
  availableTags: string[] = [];
  filteredTags: string[] = [];
  tagSearchTerm = '';
  showTagDropdown = false;
  tagsLoading = false;

  // Datos para Paso 2 - Productos
  availableProducts: any[] = []; // Productos padre disponibles
  filteredProducts: any[] = []; // Productos filtrados por búsqueda
  productSearchTerm = '';
  showProductDropdown = false;
  productsLoading = false;

  // Identificadores del producto seleccionado
  availableIdentifiers: any[] = []; // Identificadores del producto seleccionado
  filteredIdentifiers: any[] = []; // Identificadores filtrados por búsqueda
  identifierSearchTerm = '';
  showIdentifierDropdown = false;
  identifiersLoading = false;

  // Selección temporal
  selectedProduct: any = null; // Producto padre seleccionado temporalmente
  selectedIdentifier: any = null; // Identificador seleccionado temporalmente

  // Lista final de productos agregados al plan
  addedProducts: any[] = []; // Array de {product, identifier} agregados al plan

  // Propiedades para propuestas de seguros
  insuranceProposals: any[] = [];
  selectedProposal: string = '';
  showInsuranceProposals: boolean = false;
  loadingInsuranceProposals: boolean = false; // Estado de carga de propuestas
  selectedProductInsuranceConfig: any = null;

  // Payment Terms Builder
  paymentTerms: PaymentTerm[] = [];
  paymentTermsValid = false;

  // Estado para mantener la propuesta seleccionada durante navegación
  savedProposalState: { [productCode: string]: string } = {};

  // Debug logs para rastrear el flujo
  debugLogs: string[] = [];

  // Configuración de pasos
  wizardSteps = [
    {
      id: 1,
      title: 'Etiquetas',
      description: 'Define la audiencia del plan',
      icon: 'fas fa-tags',
      required: true
    },
    {
      id: 2,
      title: 'Productos',
      description: 'Selecciona tipos de producto',
      icon: 'fas fa-box',
      required: true
    },
    {
      id: 3,
      title: 'Datos Básicos',
      description: 'Información fundamental del plan',
      icon: 'fas fa-info-circle',
      required: true
    },
    {
      id: 4,
      title: 'Datos Generales',
      description: 'Configuración financiera',
      icon: 'fas fa-calculator',
      required: true
    },
    {
      id: 5,
      title: 'Filtros',
      description: 'Criterios de elegibilidad',
      icon: 'fas fa-filter',
      required: false
    },
    {
      id: 6,
      title: 'Otros',
      description: 'Configuraciones adicionales',
      icon: 'fas fa-cog',
      required: false
    },
    {
      id: 7,
      title: 'Finalizar',
      description: 'Revisión y confirmación',
      icon: 'fas fa-check-circle',
      required: true
    }
  ];

  // Glosario contextual por paso
  currentStepGlossary: any[] = [];

  // Variables específicas para filtros
  availableLocations: any[] = []; // Ubicaciones disponibles
  filteredLocations: any[] = []; // Ubicaciones filtradas por búsqueda
  locationSearchTerm = '';
  showLocationDropdown = false;
  locationsLoading = false;

  // Variables para filtros de tienda
  availableStores: any[] = [];
  filteredStores: any[] = [];
  storeSearchTerm: string = '';
  showStoreDropdown: boolean = false;
  storesLoading = false;

  // ── multicomercio: a que comercios aplica el plan ──────────────────────────
  // 🔴 La regla, y esta escrita aca porque es la que hay que respetar en los dos
  // extremos: **lista vacia = el plan aplica a TODOS los comercios.** No es un
  // "sin configurar": es la configuracion por omision, y asi es como funcionan
  // hoy los 56 planes del catalogo.
  comerciosDisponibles: any[] = [];
  comerciosFiltrados: any[] = [];
  comercioBusqueda = '';
  mostrarListaComercios = false;
  comerciosCargando = false;

  // Variables para filtros de productos del catálogo
  filteredCatalogProducts: any[] = [];
  catalogProductSearchTerm: string = '';
  showCatalogProductDropdown: boolean = false;
  catalogProductsLoading = false;

  // Variables para posicionamiento dinámico del dropdown
  dropdownTop: number = 0;
  dropdownLeft: number = 0;
  dropdownWidth: number = 300;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private userService: UserService,
    private planService: PlanService,
    private authService: AuthService,
    private merchantService: MerchantService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.initializeForms();

    this.loadAvailableTags();
    this.loadAvailableProducts();
    this.loadAvailableStores(); // Cargar tiendas para filtros
    this.cargarComercios();     // Cargar comercios para acotar el plan
    this.updateStepGlossary();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    // Paso 1: Etiquetas
    this.step1Form = this.fb.group({
      filterMode: [''], // 'whitelist' | 'blacklist' - Opcional
      selectedTags: [[], this.validateTags.bind(this)]
    });

    // Paso 2: Penalidades/Fees
    this.step2Form = this.fb.group({
      // Paso 2 es completamente opcional - no hay campos obligatorios
    });

    // Paso 3: Productos
    this.step3Form = this.fb.group({
      selectedProducts: [[], Validators.required] // Array de identificadores seleccionados
    });

    // Paso 4: Datos Básicos
    this.step4Form = this.fb.group({
      planType: ['', Validators.required], // 'cash' | 'credit'
      status: ['false', Validators.required], // 'true' | 'false' - Inactivo por defecto
      planName: ['', [Validators.required, Validators.maxLength(100)]],
      planCode: ['', [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(/^[A-Z0-9_-]+$/i) // Solo letras, números, guiones y guiones bajos
      ]],
      description: ['', Validators.maxLength(1000)],
      icon: ['fas fa-star'], // Valor por defecto
      priority: [0, [Validators.min(0), Validators.max(999)]], // Sin Validators.required - es opcional para seguros
      multicompra: [false], // Boolean - false por defecto
      startDate: ['', Validators.required],
      endDate: ['', this.validateEndDate.bind(this)]
    });

    // Paso 5: Datos Generales (solo si es crédito)
    this.step5Form = this.fb.group({
      // Pago Inicial
      initialPayment: this.fb.group({
        required: [true, Validators.required],
        calculationType: ['percentage', Validators.required], // 'percentage' | 'absolute'
        value: [0, [Validators.required, Validators.min(0)]], // Será actualizado dinámicamente según 'required'
        recalculateInstallments: [true]
      }),

      // Montos de Financiamiento
      financing: this.fb.group({
        minPurchaseAmount: [0, [Validators.required, Validators.min(0.01)]],
        maxFinanceAmount: [0, [Validators.required, Validators.min(0.01)]]
      }),

      // Plazos y Cuotas - REFACTORIZADO: Ahora se maneja con PaymentTermsBuilderComponent
      // installments: this.fb.group({
      //   options: ['', [Validators.required, Validators.pattern(/^[0-9]+(,[0-9]+)*$/)]],
      //   frequency: ['mensual', Validators.required],
      //   frequencyDays: [0]
      // }),

      // Requisitos de Cupo (van al final)
      quotaRequirements: this.fb.group({
        minQuota: [0],
        maxQuota: [0]
      })
    });

    // Paso 6: Filtros
    this.step6Form = this.fb.group({
      filterType: [''], // 'person' | 'store' - Tipo de filtro principal

      // Configuración por Persona
      personConfig: this.fb.group({
        enabled: [false],
        personType: ['natural'] // Valores: 'natural' o 'juridica'
      }),

      // Configuración por Tienda
      storeConfig: this.fb.group({
        enabled: [false],
        filterType: [''], // 'whitelist' | 'blacklist'
        selectedStores: [[]] // Array de tiendas seleccionadas
      }),

      // A que comercios aplica el plan. Vacio = todos.
      merchantConfig: this.fb.group({
        selectedMerchants: [[]]
      }),

      // Configuración por Productos del Catálogo
      productConfig: this.fb.group({
        enabled: [false],
        filterType: [''], // 'white_list' | 'black_list'
        matchMode: ['any'], // 'all' = todos deben coincidir, 'any' = al menos uno
        allowOthers: [true], // true = permite productos fuera de la lista, false = solo productos de la lista
        selectedProducts: [[]] // Array de productos seleccionados {code, name, brand, category}
      })
    });

    // Paso 7: Otros
    this.step7Form = this.fb.group({
      electronicPromissoryNote: [false],      // Pagaré Electrónico
      physicalPromissoryNote: [false],        // Pagaré Físico
      allowWeekendsAndHolidays: [false],       // Diferir cuotas en feriados
      deferInstallments: [false],             // Diferimiento de Cuotas
      clientVisible: [true]                   // Visibilidad del plan al cliente (por defecto visible)
    });

    // Paso 8: Finalizar
    this.step8Form = this.fb.group({
      termsAccepted: [false, Validators.requiredTrue],
      reviewCompleted: [false, Validators.requiredTrue]
    });
  }



  // Validador personalizado para etiquetas
  private validateTags(control: any) {
    // Las etiquetas no son obligatorias para la navegación
    // Se mantiene el validador para mostrar advertencias en la UI si es necesario
    return null;
  }

  // Métodos para obtener los pasos visibles según el tipo de plan
  /**
   * 🔴 SIN ESTO LOS PASOS NO SE PUEDEN CLICKEAR. Medido en el navegador el
   * 2026-08-21: se marcó un nodo de la lista con un atributo y a los 2,5 s el
   * atributo ya no estaba — el nodo se había recreado.
   *
   * La causa: el `*ngFor` itera sobre `getVisibleSteps()`, que es una LLAMADA A
   * FUNCIÓN. Cada ciclo de detección de cambios devuelve un array nuevo con
   * objetos nuevos, y sin `trackBy` Angular compara por identidad: concluye que
   * son todos distintos y **destruye y rehace los 8 nodos**. Y en esta pantalla
   * la detección de cambios corre todo el tiempo (el propio `*ngFor` la
   * dispara, y se ve en la consola validando el paso 1 sin parar).
   *
   * Consecuencia: un click necesita que `mousedown` y `mouseup` caigan en el
   * MISMO elemento. Acá el elemento se cambia en el medio, así que el navegador
   * nunca emite el evento `click` y `goToStep` no se llama jamás. Verificado:
   * llamando `goToStep(4)` a mano el paso cambia sin problema.
   *
   * Con `trackBy` por número, Angular reutiliza los nodos y el click funciona.
   */
  trackByPaso = (_indice: number, paso: any) => paso?.number;

  getVisibleSteps(): any[] {
    const allSteps = [
      { number: 1, title: 'Etiquetas', icon: 'fas fa-tags' },
      { number: 2, title: 'Penalidades', icon: 'fas fa-dollar-sign' },
      { number: 3, title: 'Productos', icon: 'fas fa-box' },
      { number: 4, title: 'Datos Básicos', icon: 'fas fa-info-circle' },
      { number: 5, title: 'Datos Generales', icon: 'fas fa-chart-line' },
      { number: 6, title: 'Filtros', icon: 'fas fa-filter' },
      { number: 7, title: 'Otros', icon: 'fas fa-cog' },
      { number: 8, title: 'Finalizar', icon: 'fas fa-check' }
    ];

    // Si es plan de contado, ocultar el paso 5 (Datos Generales)
    if (this.getSelectedPlanType() === 'cash') {
      return allSteps.filter(step => step.number !== 5);
    }

    return allSteps;
  }

  // Métodos para obtener el número del siguiente paso considerando los pasos ocultos
  getNextStepNumber(currentStep: number): number {
    const visibleSteps = this.getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(step => step.number === currentStep);

    if (currentIndex >= 0 && currentIndex < visibleSteps.length - 1) {
      return visibleSteps[currentIndex + 1].number;
    }

    return currentStep;
  }

  // Métodos para obtener el número del paso anterior considerando los pasos ocultos
  getPreviousStepNumber(currentStep: number): number {
    const visibleSteps = this.getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(step => step.number === currentStep);

    if (currentIndex > 0) {
      return visibleSteps[currentIndex - 1].number;
    }

    return currentStep;
  }

  // Métodos para verificar si un paso está oculto
  isStepHidden(stepNumber: number): boolean {
    const visibleSteps = this.getVisibleSteps();
    return !visibleSteps.some(step => step.number === stepNumber);
  }

  // Navegación entre pasos
  goToStep(step: number): void {
    if (this.canNavigateToStep(step) && !this.isStepHidden(step)) {
      this.currentStep = step;
      this.updateStepGlossary();
    }
  }

  nextStep(): void {
    console.log('🔄 nextStep() called - Current step:', this.currentStep);
    console.log('🔄 Sequential navigation:', this.enforceSequentialNavigation);
    console.log('🔄 Current step valid:', this.isCurrentStepValid());

    const nextStep = this.getNextStepNumber(this.currentStep);
    console.log('🔄 Next step would be:', nextStep);

    if (this.enforceSequentialNavigation) {
      // Modo secuencial: validar antes de avanzar
      const isCurrentValid = this.isCurrentStepValid();
      console.log('🔄 Current step validation:', isCurrentValid);

      if (isCurrentValid) {
        // Marcar el paso actual como completado ANTES de verificar navegación
        if (!this.completedSteps.includes(this.currentStep)) {
          this.completedSteps.push(this.currentStep);
          console.log('✅ Step marked as completed:', this.currentStep, 'Completed steps:', this.completedSteps);
        }

        // Ahora verificar si podemos navegar al siguiente paso
        if (this.canNavigateToStep(nextStep)) {
          this.currentStep = nextStep;
          this.updateStepGlossary();
          console.log('✅ Navigation successful to step:', nextStep);
        } else {
          console.log('❌ Cannot navigate to step:', nextStep, 'even after marking current as completed');
        }
      } else {
        console.log('❌ Current step is not valid, cannot advance');
      }
    } else {
      // Modo libre: navegar sin validaciones
      if (nextStep <= this.totalSteps && !this.isStepHidden(nextStep)) {
        // Marcar como completado si es válido (opcional en modo libre)
        if (this.isCurrentStepValid() && !this.completedSteps.includes(this.currentStep)) {
          this.completedSteps.push(this.currentStep);
        }

        this.currentStep = nextStep;
        this.updateStepGlossary();
        console.log('✅ Free navigation to step:', nextStep);
      } else {
        console.log('❌ Cannot navigate - step hidden or beyond total steps');
      }
    }
  }

  previousStep(): void {
    const previousStep = this.getPreviousStepNumber(this.currentStep);
    if (previousStep !== this.currentStep) {
      this.currentStep = previousStep;
      this.updateStepGlossary();
    }
  }

  // Validaciones de navegación
  /**
   * ¿Se puede llegar a este paso? Version SILENCIOSA de `canNavigateToStep`.
   *
   * 🔴 Existe por dos motivos y los dos importan:
   *
   * 1. La lista de pasos tenia `cursor-pointer` en TODOS, pero en modo
   *    secuencial `goToStep` no hace nada para los pasos que estan mas adelante
   *    que el avance. O sea: el control decia "clickeame" y no pasaba nada, sin
   *    mensaje ni pista. Un control que miente es peor que un control apagado.
   *
   * 2. `canNavigateToStep` imprime CUATRO lineas en la consola cada vez que se
   *    la llama, y la plantilla la llamaria una vez por paso en cada ciclo de
   *    deteccion de cambios: son cientos de lineas por segundo. Por eso esta
   *    version no escribe nada — la otra queda como esta, para el click.
   */
  pasoAlcanzable(numero: number): boolean {
    if (numero < 1 || numero > this.totalSteps) return false;
    if (this.isStepHidden(numero)) return false;
    if (!this.enforceSequentialNavigation) return true;
    return numero <= this.getMaxAvailableStep();
  }

  canNavigateToStep(stepNumber: number): boolean {
    console.log('🔍 canNavigateToStep() - Target step:', stepNumber, 'Current step:', this.currentStep);
    console.log('🔍 Completed steps:', this.completedSteps);
    console.log('🔍 Sequential navigation:', this.enforceSequentialNavigation);

    if (!this.enforceSequentialNavigation) {
      // Modo libre: puede navegar a cualquier paso no oculto
      const canNavigate = stepNumber >= 1 && stepNumber <= this.totalSteps && !this.isStepHidden(stepNumber);
      console.log('🔓 Free navigation - Can navigate:', canNavigate);
      return canNavigate;
    }

    // Modo secuencial: solo puede navegar a pasos completados o al siguiente paso disponible
    const maxAvailable = this.getMaxAvailableStep();
    const canNavigate = stepNumber <= maxAvailable && !this.isStepHidden(stepNumber);
    console.log('🔒 Sequential navigation - Max available step:', maxAvailable, 'Can navigate:', canNavigate);
    return canNavigate;
  }

  canGoToNextStep(): boolean {
    const nextStep = this.getNextStepNumber(this.currentStep);
    console.log('🔍 canGoToNextStep() - Current step:', this.currentStep, 'Next step:', nextStep);

    if (!this.enforceSequentialNavigation) {
      // Modo libre: siempre puede avanzar si hay siguiente paso
      const canAdvance = nextStep > this.currentStep && nextStep <= this.totalSteps && !this.isStepHidden(nextStep);
      console.log('🔓 Free navigation - Can advance:', canAdvance);
      return canAdvance;
    }

    // Modo secuencial: debe ser válido para avanzar
    const isValid = this.isCurrentStepValid();
    const hasNextStep = nextStep > this.currentStep && nextStep <= this.totalSteps;
    const canAdvance = isValid && hasNextStep && !this.isStepHidden(nextStep);

    console.log('🔒 Sequential navigation - Is valid:', isValid, 'Has next step:', hasNextStep, 'Can advance:', canAdvance);
    return canAdvance;
  }

  private getMaxAvailableStep(): number {
    // En navegación secuencial, el máximo paso disponible es:
    // - Si estamos en el paso 1 y es válido, podemos ir al paso 2
    // - Si hay pasos completados, el máximo es el último completado + 1
    // - Nunca puede ser mayor al total de pasos

    if (this.completedSteps.length === 0) {
      // Si no hay pasos completados, verificar si el paso actual es válido
      if (this.currentStep === 1 && this.isCurrentStepValid()) {
        // Si estamos en el paso 1 y es válido, podemos ir al paso 2
        const maxAvailable = Math.min(2, this.totalSteps);
        console.log('🔍 getMaxAvailableStep() - No completed steps, current step 1 is valid, max available:', maxAvailable);
        return maxAvailable;
      } else {
        // Si no estamos en el paso 1 o no es válido, solo podemos estar en el paso 1
        console.log('🔍 getMaxAvailableStep() - No completed steps, staying at step 1');
        return 1;
      }
    }

    // Si hay pasos completados, el máximo es el último completado + 1
    const maxCompleted = Math.max(...this.completedSteps);
    const maxAvailable = Math.min(maxCompleted + 1, this.totalSteps);
    console.log('🔍 getMaxAvailableStep() - Completed steps:', this.completedSteps, 'Max completed:', maxCompleted, 'Max available:', maxAvailable);
    return maxAvailable;
  }

  // Validación del paso actual
  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.validateStep1();
      case 2:
        return this.validateStep2();
      case 3:
        return this.validateStep3();
      case 4:
        return this.validateStep4();
      case 5:
        // Si es plan de contado, el paso 5 (Datos Generales) se considera válido automáticamente
        if (this.getSelectedPlanType() === 'cash') {
          return true;
        }
        return this.validateStep5();
      case 6:
        return this.validateStep6();
      case 7:
        return this.validateStep7();
      case 8:
        return this.validateStep8();
      default:
        return false;
    }
  }

  getCurrentStepForm(): FormGroup | null {
    switch (this.currentStep) {
      case 1: return this.step1Form;
      case 2: return this.step2Form;
      case 3: return this.step3Form;
      case 4: return this.step4Form;
      case 5: return this.step5Form;
      case 6: return this.step6Form;
      case 7: return this.step7Form;
      case 8: return this.step8Form;
      default: return null;
    }
  }

  // Campos faltantes para la sección dinámica
  getMissingRequiredFields(): string[] {
    const missingFields: string[] = [];
    const planType = this.getSelectedPlanType();

    switch (this.currentStep) {
      case 1:
        // El paso 1 (Etiquetas) es completamente opcional
        // No hay campos obligatorios en este paso
        break;

      case 2:
        // El paso 2 (Penalidades) es completamente opcional
        // No hay campos obligatorios en este paso
        break;

      case 3:
        if (this.addedProducts.length === 0) {
          missingFields.push('Debe seleccionar un producto');
        }
        break;

      case 4:
        if (this.step4Form.get('planType')?.invalid) {
          missingFields.push('Tipo de Plan');
        }
        if (this.step4Form.get('status')?.invalid) {
          missingFields.push('Estatus');
        }
        if (this.step4Form.get('planName')?.invalid) {
          missingFields.push('Nombre del Plan');
        }
        if (this.step4Form.get('planCode')?.invalid) {
          missingFields.push('Código del Plan');
        }
        if (this.step4Form.get('priority')?.invalid) {
          missingFields.push('Prioridad');
        }
        if (this.step4Form.get('startDate')?.invalid) {
          missingFields.push('Fecha de Inicio');
        }
        if (this.step4Form.get('endDate')?.invalid) {
          missingFields.push('Fecha de Finalización válida');
        }
        break;

      case 5:
        // Solo validar si es plan de crédito
        if (planType === 'credit') {
          // Validar Pago Inicial
          const initialRequired = this.step5Form.get('initialPayment.required')?.value;
          if (initialRequired) {
            if (!this.step5Form.get('initialPayment.calculationType')?.value) {
              missingFields.push('Forma de Cálculo del Inicial');
            }
            if (!this.step5Form.get('initialPayment.value')?.value ||
              this.step5Form.get('initialPayment.value')?.value <= 0) {
              missingFields.push('Valor del Pago Inicial');
            }
          }

          // Validar Montos de Financiamiento
          if (!this.step5Form.get('financing.minPurchaseAmount')?.value ||
            this.step5Form.get('financing.minPurchaseAmount')?.value <= 0) {
            missingFields.push('Monto Mínimo de Compra');
          }
          if (!this.step5Form.get('financing.maxFinanceAmount')?.value ||
            this.step5Form.get('financing.maxFinanceAmount')?.value <= 0) {
            missingFields.push('Monto Máximo a Financiar');
          }

          // Validar coherencia de montos
          const minFinance = this.calculateMinFinanceAmount();
          const maxFinance = this.step5Form.get('financing.maxFinanceAmount')?.value || 0;
          if (maxFinance > 0 && minFinance > 0 && maxFinance <= minFinance) {
            missingFields.push('El monto máximo debe ser mayor al mínimo calculado');
          }

          // Validar Plazos y Cuotas (Nueva lógica)
          if (!this.paymentTermsValid || this.paymentTerms.length === 0) {
            missingFields.push('Debe configurar al menos una opción de cuotas');
          }

          // Validaciones antiguas eliminadas
          /*
          const installmentOptionsControl = this.step5Form.get('installments.options');
          if (!installmentOptionsControl?.value?.trim()) {
            missingFields.push('Opciones de Cuotas');
          } else if (installmentOptionsControl.errors?.['pattern']) {
            missingFields.push('Opciones de Cuotas con formato válido (números separados por comas)');
          } else {
            const installmentOptions = this.getInstallmentOptions();
            if (installmentOptions.length === 0) {
              missingFields.push('Opciones de Cuotas válidas');
            }
          }

          if (!this.step5Form.get('installments.frequency')?.value) {
            missingFields.push('Frecuencia de Pago');
          }
          */

          // Validar regla de oro para inicial absoluta
          const calculationType = this.step5Form.get('initialPayment.calculationType')?.value;
          const initialValue = this.step5Form.get('initialPayment.value')?.value || 0;
          const minPurchaseAmount = this.step5Form.get('financing.minPurchaseAmount')?.value || 0;

          if (initialRequired && calculationType === 'absolute' &&
            initialValue > 0 && minPurchaseAmount > 0 &&
            initialValue >= minPurchaseAmount) {
            missingFields.push('Inicial Absoluta debe ser menor al Monto Mínimo de Compra');
          }
        }
        break;

      case 6:
        // Paso 6 (Filtros) - Validaciones si las hay
        break;

      case 7:
        // Paso 7 (Otros) - Validaciones si las hay
        break;

      case 8:
        if (this.step8Form.get('termsAccepted')?.invalid) {
          missingFields.push('Aceptar Términos y Condiciones');
        }
        if (this.step8Form.get('reviewCompleted')?.invalid) {
          missingFields.push('Completar Revisión');
        }
        break;
    }

    return missingFields;
  }

  private getFieldDisplayName(controlName: string): string {
    const fieldNames: { [key: string]: string } = {
      'filterMode': 'Modo de Filtro',
      'selectedTags': 'Etiquetas',
      'selectedProducts': 'Productos',
      'planType': 'Tipo de Plan',
      'planName': 'Nombre del Plan',
      'planCode': 'Código del Plan',
      'priority': 'Prioridad',
      'termsAccepted': 'Aceptación de Términos',
      'reviewCompleted': 'Revisión Completada'
    };

    return fieldNames[controlName] || controlName;
  }

  // Manejo de pasos completados
  private markStepAsCompleted(stepNumber: number): void {
    if (!this.completedSteps.includes(stepNumber)) {
      this.completedSteps.push(stepNumber);
    }
  }

  isStepCompleted(stepNumber: number): boolean {
    return this.completedSteps.includes(stepNumber);
  }

  getStepStatus(stepNumber: number): 'completed' | 'current' | 'pending' {
    if (this.isStepCompleted(stepNumber)) return 'completed';
    if (stepNumber === this.currentStep) return 'current';
    return 'pending';
  }

  // Progreso del wizard
  getProgressPercentage(): number {
    return Math.round((this.currentStep / this.totalSteps) * 100);
  }

  // Glosario contextual
  private updateStepGlossary(): void {
    switch (this.currentStep) {
      case 1:
        this.currentStepGlossary = [
          {
            term: 'Lista Blanca',
            definition: 'Solo las personas con estas etiquetas pueden acceder al plan'
          },
          {
            term: 'Lista Negra',
            definition: 'Las personas con estas etiquetas NO pueden acceder al plan'
          },
          {
            term: 'Etiquetas',
            definition: 'Clasificaciones asignadas a personas para segmentación'
          },
          {
            term: 'Audiencia Estimada',
            definition: 'Número aproximado de personas que cumplen con los criterios'
          }
        ];
        break;

      case 2:
        this.currentStepGlossary = [
          {
            term: 'Tipo de Producto',
            definition: 'Categoría específica de producto/servicio al que aplica el plan'
          },
          {
            term: 'Producto Padre',
            definition: 'Categoría principal que contiene múltiples identificadores'
          },
          {
            term: 'Identificador',
            definition: 'Código específico del producto dentro de una categoría'
          },
          {
            term: 'Un Solo Producto',
            definition: 'Cada plan puede aplicar únicamente a un tipo de producto específico'
          }
        ];
        break;

      case 3:
        this.currentStepGlossary = [
          {
            term: 'Plan de Contado',
            definition: 'Pago único del 100% del valor. Sin cuotas ni financiamiento.'
          },
          {
            term: 'Plan a Crédito',
            definition: 'Financiamiento con pagos fraccionados en el tiempo.'
          },
          {
            term: 'Código del Plan',
            definition: 'Identificador único e inmutable del sistema para integraciones.'
          },
          {
            term: 'Prioridad',
            definition: 'Orden de sugerencia cuando un cliente es elegible para múltiples planes.'
          },
          {
            term: 'Multicompra',
            definition: 'Permite que un cliente use el mismo plan en múltiples ocasiones.'
          },
          {
            term: 'Estado Inactivo',
            definition: 'Plan creado pero no disponible para clientes. Recomendado para revisión.'
          },
          {
            term: 'Estado Activo',
            definition: 'Plan disponible y visible para clientes elegibles.'
          },
          {
            term: 'Fecha de Vigencia',
            definition: 'Período durante el cual el plan está disponible para uso.'
          }
        ];
        break;

      case 4:
        this.currentStepGlossary = [
          {
            term: 'Pago Inicial',
            definition: 'Cantidad que el cliente paga por adelantado antes de las cuotas'
          },
          {
            term: 'Porcentaje vs Monto',
            definition: 'Inicial calculada como % del total o cantidad fija en USD'
          },
          {
            term: 'Recalcular Cuotas',
            definition: 'Si activo, resta la inicial del monto total para calcular cuotas'
          },
          {
            term: 'Opciones de Cuotas',
            definition: 'Número de pagos que puede elegir el cliente (ej: 2,4,6)'
          },
          {
            term: 'Frecuencia',
            definition: 'Periodicidad de los pagos: semanal, quincenal, mensual'
          },
          {
            term: 'Cupo de Crédito',
            definition: 'Capacidad crediticia asignada al cliente en el sistema'
          },
          {
            term: 'Monto a Financiar',
            definition: 'Cantidad que se pagará en cuotas, puede incluir o excluir la inicial'
          },
          {
            term: 'Regla de Oro',
            definition: 'Inicial absoluta debe ser menor al monto mínimo de compra'
          },
          {
            term: 'Diferimiento de Cuotas',
            definition: 'Mover automáticamente las cuotas que caigan en fines de semana o feriados al siguiente día hábil'
          }
        ];
        break;

      case 5:
        this.currentStepGlossary = [
          {
            term: 'Filtros Demográficos',
            definition: 'Restricciones basadas en características de la persona'
          },
          {
            term: 'Filtros Geográficos',
            definition: 'Restricciones basadas en ubicación o tienda'
          }
        ];
        break;

      case 6:
        this.currentStepGlossary = [
          {
            term: 'Pagaré Electrónico',
            definition: 'Documento legal digital que formaliza la deuda del cliente'
          },
          {
            term: 'Pagaré Físico',
            definition: 'Documento legal impreso que formaliza la deuda del cliente'
          },
          {
            term: 'Diferimiento de Cuotas',
            definition: 'Mover automáticamente las cuotas que caigan en fines de semana o feriados al siguiente día hábil'
          },
          {
            term: 'Configuración Legal',
            definition: 'Aspectos legales y administrativos del plan de crédito'
          }
        ];
        break;

      case 7:
        this.currentStepGlossary = [
          {
            term: 'Revisión Final',
            definition: 'Verificación de todos los parámetros antes de crear el plan'
          },
          {
            term: 'Términos y Condiciones',
            definition: 'Aceptación formal de la configuración del plan'
          }
        ];
        break;

      default:
        this.currentStepGlossary = [];
    }
  }

  // Acciones principales
  saveAndExit(): void {
    this.isSaving = true;
    // Implementar lógica de guardado
    setTimeout(() => {
      this.isSaving = false;
      this.router.navigate(['/console/plans']);
    }, 1000);
  }

  goBack(): void {
    this.router.navigate(['/console/plans']);
  }

  finalizePlan(): void {
    if (!this.step7Form.valid) {
      console.log('❌ Form validation failed');
      return;
    }

    this.isSaving = true;

    try {
      // Obtener los datos del plan en formato de base de datos
      const planData = this.getDatabaseFormatJSON();

      console.log('🚀 Creating plan with data:', planData);

      // TEMPORAL: Verificar campos específicos que están fallando
      console.log('🔍 VERIFICACIÓN DE CAMPOS REQUERIDOS:');
      console.log('  - initial.amount:', planData.initial?.amount);
      console.log('  - date:', planData.date);
      console.log('  - version.code:', planData.version?.code);
      console.log('  - version.name:', planData.version?.name);
      console.log('  - version.number:', planData.version?.number);
      console.log('  - version.description:', planData.version?.description);
      console.log('  - version.date_init_version:', planData.version?.date_init_version);
      console.log('  - created_by.user:', planData.created_by?.user);
      console.log('  - created_by.date:', planData.created_by?.date);
      console.log('📋 JSON COMPLETO A ENVIAR:', JSON.stringify(planData, null, 2));

      // Llamar al servicio para crear el plan
      this.planService.createPlan(planData).subscribe({
        next: (response: any) => {
          console.log('✅ Plan created successfully:', response);
          this.isSaving = false;

          alertifyjs.success('Plan creado exitosamente');
          // Redirigir al dashboard de planes
          this.router.navigate(['/console/plans']);
        },
        error: (error: any) => {
          console.error('❌ Error creating plan:', error);
          console.error('❌ Error details:', JSON.stringify(error, null, 2));
          this.isSaving = false;

          // Extraer el mensaje de error específico del backend
          const backendMessage = error?.error?.msg ||
            error?.error?.message ||
            error?.msg ||
            error?.message ||
            'Intente nuevamente.';

          alertifyjs.error(`Error al crear el plan: ${backendMessage}`);
        }
      });

    } catch (error) {
      console.error('❌ Error preparing plan data:', error);
      this.isSaving = false;
      // TODO: Mostrar mensaje de error al usuario
    }
  }

  // Métodos helper para el Step 7: Finalizar

  /**
   * Obtiene un resumen de los datos del plan para mostrar en Step 7
   */
  getPlanSummary(): any {
    const planType = this.getSelectedPlanType();
    const isCredit = planType === 'credit' || planType === 'suscripcion';

    // Determinar el texto del tipo
    let typeLabel = 'Contado';
    if (planType === 'credit') typeLabel = 'Crédito';
    else if (planType === 'suscripcion') typeLabel = 'Suscripción';

    return {
      // Información básica
      name: this.step4Form?.get('planName')?.value || 'Sin nombre',
      code: this.step4Form?.get('planCode')?.value || 'Sin código',
      type: typeLabel,
      status: this.step4Form?.get('status')?.value === 'true' ? 'Activo' : 'Inactivo',
      description: this.step4Form?.get('description')?.value || 'Sin descripción',

      // Productos
      productCount: this.addedProducts.length,
      product: this.addedProducts.length > 0 ? `${this.addedProducts[0].product.name} (${this.addedProducts[0].identifier.name})` : 'Ninguno',

      // Configuraciones específicas de crédito/suscripción
      ...(isCredit && {
        minPurchaseAmount: this.step5Form?.get('financing.minPurchaseAmount')?.value || 0,
        maxFinanceAmount: this.step5Form?.get('financing.maxFinanceAmount')?.value || 0,
        installmentOptions: this.paymentTerms.map(t => `${t.installments} (${t.frequency})`),
        frequency: this.paymentTerms.length > 0 ? 'Variada' : 'No definida',
        initialPaymentRequired: this.step5Form?.get('initialPayment.required')?.value || false,
        initialPaymentValue: this.step5Form?.get('initialPayment.value')?.value || 0,
        initialPaymentType: this.step5Form?.get('initialPayment.calculationType')?.value || 'percentage'
      }),

      // Filtros
      hasPersonFilters: this.arePersonFiltersEnabled(),
      hasStoreFilters: this.areStoreFiltersEnabled(),

      // Configuraciones adicionales (Step 6)
      electronicPromissoryNote: this.step6Form?.get('electronicPromissoryNote')?.value || false,
      physicalPromissoryNote: this.step6Form?.get('physicalPromissoryNote')?.value || false,
      allowWeekendsAndHolidays: this.step6Form?.get('allowWeekendsAndHolidays')?.value || false,
      deferInstallments: this.step6Form?.get('deferInstallments')?.value || false,

      // Fechas
      startDate: this.step3Form?.get('startDate')?.value,
      endDate: this.step3Form?.get('endDate')?.value
    };
  }

  /**
   * Genera el texto de conformidad para mostrar al usuario
   */
  getConformityText(): string {
    const summary = this.getPlanSummary();
    const isCredit = summary.type === 'Crédito' || summary.type === 'Suscripción';

    let text = `Está a punto de crear el plan "${summary.name}" con código "${summary.code}". `;
    text += `Este es un plan de ${summary.type.toLowerCase()} que será creado en estado ${summary.status.toLowerCase()}. `;

    if (summary.productCount > 0) {
      text += `El plan aplica al producto: ${summary.product}. `;
    }

    if (isCredit) {
      text += `Para planes de ${summary.type.toLowerCase()}, el monto mínimo de compra es $${summary.minPurchaseAmount.toLocaleString()} `;
      text += `y el monto máximo a financiar es $${summary.maxFinanceAmount.toLocaleString()}. `;

      if (summary.installmentOptions?.length > 0) {
        text += `Las opciones de cuotas disponibles son: ${summary.installmentOptions.join(', ')} cuotas `;
        text += `con frecuencia ${summary.frequency.toLowerCase()}. `;
      }

      if (summary.initialPaymentRequired) {
        const paymentType = summary.initialPaymentType === 'percentage' ? '%' : '$';
        text += `Se requiere un pago inicial de ${summary.initialPaymentValue}${paymentType}. `;
      }
    }

    if (summary.hasPersonFilters || summary.hasStoreFilters) {
      text += 'El plan tiene filtros configurados que limitarán su disponibilidad. ';
    }

    if (summary.electronicPromissoryNote || summary.physicalPromissoryNote) {
      text += 'Se generarán pagarés automáticamente según la configuración establecida. ';
    }

    text += 'Una vez creado, el plan estará disponible según su configuración de estado y filtros.';

    return text;
  }

  // Métodos para estilos dinámicos del template
  getStepNavClasses(stepId: number): string {
    const status = this.getStepStatus(stepId);
    const baseClasses = 'flex items-center p-3 rounded-lg cursor-pointer transition-colors';

    switch (status) {
      // Las clases salen del sistema de diseno (.kb-* en styles.css). Antes eran
      // colores crudos y cada estado usaba una familia distinta —verde, cian,
      // gris— sin que el color quisiera decir nada mas que "otro paso".
      case 'completed':
        return 'border border-border bg-muted/40 hover:bg-accent/60';
      case 'current':
        return 'border border-primary/30 bg-primary/10';
      default:
        return 'border border-transparent hover:bg-accent/60';
    }
  }

  getStepIconClasses(stepId: number): string {
    const status = this.getStepStatus(stepId);

    switch (status) {
      case 'completed':
        return 'kb-step-bolita-hecha';
      case 'current':
        return 'kb-step-bolita-activa';
      default:
        return 'bg-muted text-muted-foreground';
    }
  }

  getStepTitleClasses(stepId: number): string {
    const status = this.getStepStatus(stepId);

    switch (status) {
      case 'completed':
        return 'text-foreground';
      case 'current':
        return 'font-semibold text-primary';
      default:
        return 'text-muted-foreground';
    }
  }

  getCurrentStepTitle(): string {
    const currentStepConfig = this.wizardSteps.find(step => step.id === this.currentStep);
    return currentStepConfig?.title || '';
  }

  getCurrentStepDescription(): string {
    const currentStepConfig = this.wizardSteps.find(step => step.id === this.currentStep);
    return currentStepConfig?.description || '';
  }

  getCurrentStepIcon(): string {
    const currentStepConfig = this.wizardSteps.find(step => step.id === this.currentStep);
    return currentStepConfig?.icon || 'fas fa-circle';
  }

  getCurrentStepIconClasses(): string {
    const status = this.getStepStatus(this.currentStep);

    switch (status) {
      case 'completed':
        return 'kb-step-bolita-hecha';
      case 'current':
        return 'kb-step-bolita-activa';
      default:
        return 'bg-muted text-muted-foreground';
    }
  }

  getNextButtonClasses(): string {
    if (!this.enforceSequentialNavigation) {
      // Modo libre: siempre habilitado si hay siguiente paso
      const nextStep = this.getNextStepNumber(this.currentStep);
      if (nextStep <= this.totalSteps && !this.isStepHidden(nextStep)) {
        return 'kb-btn-primary';
      } else {
        return 'kb-btn-outline cursor-not-allowed opacity-50';
      }
    }

    // Modo secuencial: depende de la validación
    if (this.isCurrentStepValid()) {
      return 'kb-btn-primary';
    } else {
      return 'kb-btn-outline cursor-not-allowed opacity-50';
    }
  }

  isStepCompletedSuccessfully(): boolean {
    const currentStepConfig = this.wizardSteps.find(step => step.id === this.currentStep);
    return currentStepConfig?.required === true &&
      this.getMissingRequiredFields().length === 0 &&
      this.isCurrentStepValid();
  }

  // Métodos para manejo de etiquetas en Paso 1
  async loadAvailableTags(): Promise<void> {
    try {
      this.tagsLoading = true;

      // Cargar etiquetas desde la API de códigos
      const codesResponse = await this.userService.getAllCodes();

      if (codesResponse?.data && Array.isArray(codesResponse.data)) {
        // Filtrar solo códigos con categoría "person" y extraer códigos únicos
        this.availableTags = codesResponse.data
          .filter((item: any) => item.category === 'person')
          .map((item: any) => item.code)
          .filter(Boolean);
        this.filteredTags = [...this.availableTags];
      } else {
        // Si no hay datos de la API, dejar arrays vacíos
        this.availableTags = [];
        this.filteredTags = [];
      }

    } catch (error) {
      console.error('Error loading tags:', error);
      // En caso de error, dejar arrays vacíos - no usar datos de ejemplo
      this.availableTags = [];
      this.filteredTags = [];
    } finally {
      this.tagsLoading = false;
    }
  }

  onTagSearch(event: any): void {
    const input = event.target as HTMLInputElement;
    this.tagSearchTerm = input.value;
    this.filterTags();
    this.showTagDropdown = this.tagSearchTerm.length > 0 && this.filteredTags.length > 0;
  }

  filterTags(): void {
    if (!this.tagSearchTerm.trim()) {
      this.filteredTags = [...this.availableTags];
      this.showTagDropdown = false;
      return;
    }

    const searchTerm = this.tagSearchTerm.toLowerCase();
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];

    this.filteredTags = this.availableTags.filter(tag =>
      tag.toLowerCase().includes(searchTerm) &&
      !selectedTags.includes(tag)
    );
  }

  selectTag(tag: string): void {
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];

    if (!selectedTags.includes(tag)) {
      const updatedTags = [...selectedTags, tag];
      this.step1Form.patchValue({ selectedTags: updatedTags });
    }

    // Limpiar búsqueda y cerrar dropdown
    this.clearTagSearch();
  }

  removeTag(tagToRemove: string): void {
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];
    const updatedTags = selectedTags.filter((tag: string) => tag !== tagToRemove);
    this.step1Form.patchValue({ selectedTags: updatedTags });

    // Actualizar filtros para mostrar la etiqueta removida nuevamente
    this.filterTags();
  }

  clearTagSearch(): void {
    this.tagSearchTerm = '';
    this.showTagDropdown = false;
    this.filteredTags = [...this.availableTags];

    // Limpiar el input
    const searchInput = document.querySelector('input[placeholder="Buscar etiquetas..."]') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
  }

  hideTagDropdown(): void {
    // Delay para permitir que el click en una etiqueta se procese primero
    setTimeout(() => {
      this.showTagDropdown = false;
    }, 150);
  }

  // Método para abrir el dropdown cuando se hace focus
  onTagSearchFocus(): void {
    if (this.tagSearchTerm.length > 0 && this.filteredTags.length > 0) {
      this.showTagDropdown = true;
    } else if (this.tagSearchTerm.length === 0 && this.availableTags.length > 0) {
      this.filteredTags = [...this.availableTags];
      const selectedTags = this.step1Form.get('selectedTags')?.value || [];
      this.filteredTags = this.filteredTags.filter(tag => !selectedTags.includes(tag));
      this.showTagDropdown = this.filteredTags.length > 0;
    }
  }

  getEstimatedAudience(): number {
    const selectedTags = this.step1Form.get('selectedTags')?.value || [];
    const filterMode = this.step1Form.get('filterMode')?.value;

    if (!filterMode) return 0;

    // Mock calculation - en producción esto vendría de una API
    const baseUsers = 2000;

    if (filterMode === 'whitelist') {
      // Lista blanca: menos usuarios mientras más etiquetas específicas
      const reductionFactor = Math.max(0.1, 1 - (selectedTags.length * 0.15));
      return Math.floor(baseUsers * reductionFactor);
    } else {
      // Lista negra: más usuarios mientras más etiquetas excluidas
      const reductionFactor = Math.min(0.9, selectedTags.length * 0.1);
      return Math.floor(baseUsers * (1 - reductionFactor));
    }
  }

  // Métodos para Paso 2 - Productos
  async loadAvailableProducts(): Promise<void> {
    try {
      this.productsLoading = true;

      // Cargar productos padre desde la API
      const productTypesResponse = await this.userService.getTypesProductsInvoices();

      if (productTypesResponse?.resp && Array.isArray(productTypesResponse.resp)) {
        // Procesar y organizar los productos padre
        this.availableProducts = productTypesResponse.resp.map((item: any) => ({
          code: item.params?.code || '',
          name: item.params?.name || item.params?.code || 'Sin nombre',
          description: item.params?.description || '',
          products: item.params?.products || [], // Identificadores hijos
          // Guardar información de seguros del producto
          insurance: {
            is_insurance_product: item.params?.insurance?.is_insurance_product || false,
            insurer_code: item.params?.insurance?.insurer_code || null
          }
        })).filter((product: any) => product.code); // Solo productos con código válido

        console.log('📦 Productos cargados con información de seguros (CREACIÓN):', this.availableProducts);
        this.filteredProducts = [...this.availableProducts];
      } else {
        this.availableProducts = [];
        this.filteredProducts = [];
      }

    } catch (error) {
      console.error('Error loading products:', error);
      this.availableProducts = [];
      this.filteredProducts = [];
    } finally {
      this.productsLoading = false;
    }
  }

  onProductSearch(event: any): void {
    const input = event.target as HTMLInputElement;
    this.productSearchTerm = input.value;
    this.filterProducts();
    this.showProductDropdown = this.productSearchTerm.length > 0 && this.filteredProducts.length > 0;
  }

  filterProducts(): void {
    if (!this.productSearchTerm.trim()) {
      this.filteredProducts = [...this.availableProducts];
      this.showProductDropdown = false;
      return;
    }

    const searchTerm = this.productSearchTerm.toLowerCase();
    this.filteredProducts = this.availableProducts.filter(product =>
      product.name.toLowerCase().includes(searchTerm) ||
      product.code.toLowerCase().includes(searchTerm) ||
      (product.description && product.description.toLowerCase().includes(searchTerm))
    );
  }

  selectProduct(product: any): void {
    this.selectedProduct = product;
    this.productSearchTerm = product.name;
    this.showProductDropdown = false;

    // Cargar identificadores del producto seleccionado
    this.loadProductIdentifiers(product);

    // Limpiar selección de identificador anterior
    this.selectedIdentifier = null;
    this.identifierSearchTerm = '';
    this.showIdentifierDropdown = false;

    // NOTA: Ya no verificamos seguros aquí, lo haremos cuando se seleccione el identificador
    this.resetInsuranceProposals();
  }

  loadProductIdentifiers(product: any): void {
    this.identifiersLoading = true;

    // Los identificadores están en product.products
    if (product.products && Array.isArray(product.products)) {
      this.availableIdentifiers = product.products.map((identifier: any) => ({
        code: identifier.code || '',
        name: identifier.name || identifier.code || 'Sin nombre',
        description: identifier.description || '',
        insurance_product: identifier.insurance_product || null // IMPORTANTE: Campo para productos de seguros
      })).filter((identifier: any) => identifier.code);

      console.log('📦 Identificadores cargados (CREACIÓN):', this.availableIdentifiers);
    } else {
      this.availableIdentifiers = [];
    }

    this.filteredIdentifiers = [...this.availableIdentifiers];
    this.identifiersLoading = false;
  }

  private async checkAndLoadInsuranceProposalsAfterIdentifierSelection() {
    try {
      this.addDebugLog(`🔍 Verificando configuración de seguros después de seleccionar identificador`);
      this.addDebugLog(`📋 Producto seleccionado: ${this.selectedProduct?.name} (${this.selectedProduct?.code})`);
      this.addDebugLog(`📋 Identificador seleccionado: ${this.selectedIdentifier?.name} (${this.selectedIdentifier?.code})`);

      // Obtener la configuración completa del producto para verificar si es de seguros
      const productConfig = await this.userService.getRemanenteNeteo(this.selectedProduct.code);
      this.addDebugLog(`📋 Configuración del producto recibida: ${JSON.stringify(productConfig)}`);

      if (productConfig && productConfig.insurance && productConfig.insurance.is_insurance_product) {
        this.addDebugLog(`✅ Producto ES de seguros: ${JSON.stringify(productConfig.insurance)}`);
        this.selectedProductInsuranceConfig = productConfig.insurance;
        await this.loadInsuranceProposals(this.selectedProduct.code);
        this.showInsuranceProposals = true;
        this.addDebugLog('✅ Select de propuestas mostrado DESPUÉS de seleccionar identificador');
      } else {
        this.addDebugLog(`❌ Producto NO es de seguros o no tiene configuración. Insurance: ${productConfig?.insurance}`);
        this.resetInsuranceProposals();
      }
    } catch (error) {
      this.addDebugLog(`❌ Error verificando configuración de seguros después de identificador: ${error}`);
      this.resetInsuranceProposals();
    }
  }

  private addDebugLog(message: string) {
    const timestamp = new Date().toLocaleTimeString();
    this.debugLogs.unshift(`[${timestamp}] ${message}`);
    console.log(`[DEBUG] ${message}`);

    // Mantener solo los últimos 20 logs
    if (this.debugLogs.length > 20) {
      this.debugLogs = this.debugLogs.slice(0, 20);
    }
  }

  private async loadInsuranceProposals(productCode: string) {
    // Activar estado de carga
    this.loadingInsuranceProposals = true;

    try {
      // Obtener el insurance_product del identificador seleccionado
      let insuranceProductCode = 'AP'; // Valor por defecto

      if (this.selectedIdentifier && this.selectedIdentifier.insurance_product) {
        insuranceProductCode = this.selectedIdentifier.insurance_product;
        this.addDebugLog(`📋 Usando insurance_product del identificador: ${insuranceProductCode}`);
      } else {
        this.addDebugLog(`⚠️ Identificador no tiene insurance_product, usando valor por defecto: ${insuranceProductCode}`);
      }

      // Obtener el insurer_code del producto seleccionado para pasarlo como insurance_code
      let insuranceCode = null;
      if (this.selectedProduct && this.selectedProduct.insurance && this.selectedProduct.insurance.insurer_code) {
        insuranceCode = this.selectedProduct.insurance.insurer_code;
        this.addDebugLog(`📋 Usando insurance_code del producto (insurer_code): ${insuranceCode}`);
      } else {
        this.addDebugLog(`⚠️ Producto no tiene insurer_code`);
      }

      console.log('🔍 DEBUG (CREACIÓN) - selectedIdentifier completo:', this.selectedIdentifier);
      console.log('🔍 DEBUG (CREACIÓN) - insurance_product:', insuranceProductCode);
      console.log('🔍 DEBUG (CREACIÓN) - selectedProduct.insurance:', this.selectedProduct?.insurance);
      console.log('🔍 DEBUG (CREACIÓN) - insurance_code:', insuranceCode);

      this.addDebugLog(`🚀 Cargando propuestas para insurance_product: ${insuranceProductCode}, insurance_code: ${insuranceCode}`);
      const response = await this.userService.getInsuranceProposals(insuranceProductCode, insuranceCode);
      this.addDebugLog(`📦 Propuestas recibidas RAW: ${JSON.stringify(response)}`);

      let proposalsArray: any[] = [];

      if (response && response.resp) {
        proposalsArray = response.resp;
        this.addDebugLog(`✅ Propuestas desde response.resp: ${proposalsArray.length} items`);
      } else if (response && response.data) {
        proposalsArray = response.data;
        this.addDebugLog(`✅ Propuestas desde response.data: ${proposalsArray.length} items`);
      } else if (Array.isArray(response)) {
        proposalsArray = response;
        this.addDebugLog(`✅ Propuestas como array: ${proposalsArray.length} items`);
      } else if (response && typeof response === 'object') {
        // Intentar extraer de diferentes estructuras posibles
        proposalsArray = response.proposals || response.items || response.result || [];
        this.addDebugLog(`✅ Propuestas desde object property: ${proposalsArray.length} items`);
      } else {
        proposalsArray = [];
        this.addDebugLog('⚠️ No se encontraron propuestas en ninguna estructura conocida');
      }

      // Normalizar el formato de las propuestas
      this.insuranceProposals = proposalsArray.map((proposal: any) => ({
        code: proposal.id || proposal.code || proposal.cd_option || proposal._id || '',
        name: proposal.name || proposal.nombre || proposal.title || 'Sin nombre',
        description: proposal.description || proposal.descripcion || proposal.detail || '',
        mtPrima: proposal.mtPrima || proposal.mt_prima || proposal.prima || proposal.amount || '',
        deUso: proposal.deUso || proposal.de_uso || proposal.uso || ''
      })).filter((proposal: any) => proposal.code);

      this.addDebugLog(`📋 Propuestas normalizadas: ${this.insuranceProposals.length} items`);
      
      if (this.insuranceProposals.length > 0) {
        this.addDebugLog(`📋 Primera propuesta: ${JSON.stringify(this.insuranceProposals[0])}`);
      } else {
        this.addDebugLog('⚠️ No se encontraron propuestas para este producto de seguros');
        console.warn('No hay propuestas disponibles para el producto de seguros seleccionado');
      }

    } catch (error) {
      this.addDebugLog(`❌ Error cargando propuestas: ${error}`);
      console.error('Error al cargar propuestas de seguros:', error);
      this.insuranceProposals = [];
    } finally {
      // Desactivar estado de carga siempre (éxito o error)
      this.loadingInsuranceProposals = false;
    }
  }

  private resetInsuranceProposals() {
    this.insuranceProposals = [];
    this.selectedProposal = '';
    this.showInsuranceProposals = false;
    this.selectedProductInsuranceConfig = null;
  }

  onProposalChange(event: any) {
    const selectedValue = event.target.value;
    console.log('🔄 Propuesta cambiada (CREACIÓN):', {
      eventValue: selectedValue,
      selectedProposal: this.selectedProposal
    });

    // Forzar actualización del valor
    this.selectedProposal = selectedValue;

    console.log('✅ Propuesta actualizada (CREACIÓN):', {
      selectedProposal: this.selectedProposal
    });
  }

  onIdentifierSearch(event: any): void {
    const input = event.target as HTMLInputElement;
    this.identifierSearchTerm = input.value;
    this.filterIdentifiers();
    this.showIdentifierDropdown = this.identifierSearchTerm.length > 0 && this.filteredIdentifiers.length > 0;
  }

  filterIdentifiers(): void {
    if (!this.identifierSearchTerm.trim()) {
      this.filteredIdentifiers = [...this.availableIdentifiers];
      this.showIdentifierDropdown = false;
      return;
    }

    const searchTerm = this.identifierSearchTerm.toLowerCase();
    this.filteredIdentifiers = this.availableIdentifiers.filter(identifier =>
      identifier.name.toLowerCase().includes(searchTerm) ||
      identifier.code.toLowerCase().includes(searchTerm) ||
      (identifier.description && identifier.description.toLowerCase().includes(searchTerm))
    );
  }

  selectIdentifier(identifier: any): void {
    console.log('🎯 IDENTIFICADOR SELECCIONADO (CREACIÓN):', identifier);
    console.log('🎯 insurance_product del identificador:', identifier.insurance_product);

    this.selectedIdentifier = identifier;
    this.identifierSearchTerm = identifier.name;
    this.showIdentifierDropdown = false;

    // Verificar si el producto seleccionado es de seguros y cargar propuestas
    // Solo cuando tenemos tanto producto como identificador seleccionados
    if (this.selectedProduct && this.selectedIdentifier) {
      this.checkAndLoadInsuranceProposalsAfterIdentifierSelection();
    }
  }

  addProductToList(): void {
    if (!this.selectedProduct || !this.selectedIdentifier) {
      this.addDebugLog('❌ No se puede agregar producto: producto o identificador no seleccionado');
      return;
    }

    this.addDebugLog(`➕ Agregando producto: ${this.selectedProduct.name}, identificador: ${this.selectedIdentifier.name}, propuesta: ${this.selectedProposal}`);

    // CAMBIO: Solo permitir un producto, reemplazar si ya existe uno
    // Limpiar productos existentes ya que solo se permite uno
    this.addedProducts = [];

    // Buscar el objeto completo de la propuesta seleccionada
    let insuranceProposalData = null;
    if (this.selectedProposal && this.insuranceProposals.length > 0) {
      const foundProposal = this.insuranceProposals.find(p => p.code === this.selectedProposal);
      if (foundProposal) {
        insuranceProposalData = {
          code: foundProposal.code,
          name: foundProposal.name,
          mtPrima: foundProposal.mtPrima
        };
        console.log('✅ Propuesta encontrada:', insuranceProposalData);
      }
    }

    // Agregar el nuevo producto (solo uno permitido)
    const newProductItem = {
      product: { ...this.selectedProduct },
      identifier: { ...this.selectedIdentifier },
      insuranceProposal: insuranceProposalData // Incluir objeto completo de propuesta si existe
    };

    this.addedProducts.push(newProductItem);
    this.addDebugLog(`✅ Producto agregado exitosamente. Total productos: ${this.addedProducts.length}`);
    console.log('✅ Producto agregado. addedProducts:', this.addedProducts);
    console.log('✅ insuranceProposal en producto agregado:', newProductItem.insuranceProposal);

    // Actualizar validaciones de prioridad según tipo de producto
    this.updatePriorityValidation();

    // Actualizar el formulario con el identificador seleccionado (string, no array)
    const selectedIdentifier = this.addedProducts[0].identifier.code;
    this.step2Form.patchValue({ selectedProducts: [selectedIdentifier] }); // Mantenemos array internamente para compatibilidad

    // CAMBIO: NO limpiar la selección para mantener visible el select de propuestas
    // Solo limpiar los campos de búsqueda y dropdowns
    this.productSearchTerm = '';
    this.identifierSearchTerm = '';
    this.showProductDropdown = false;
    this.showIdentifierDropdown = false;

    console.log('✅ Selección mantenida para seguros. selectedProposal:', this.selectedProposal);
  }

  removeProductFromList(index: number): void {
    this.addedProducts.splice(index, 1);

    // Actualizar validaciones de prioridad
    this.updatePriorityValidation();

    // Actualizar el formulario
    const selectedIdentifiers = this.addedProducts.map(item => item.identifier.code);
    this.step2Form.patchValue({ selectedProducts: selectedIdentifiers });
  }

  clearProductSelection(): void {
    this.selectedProduct = null;
    this.selectedIdentifier = null;
    this.productSearchTerm = '';
    this.identifierSearchTerm = '';
    this.showProductDropdown = false;
    this.showIdentifierDropdown = false;
    this.availableIdentifiers = [];
    this.filteredIdentifiers = [];

    // Limpiar configuración de seguros cuando se limpia la selección completa
    this.resetInsuranceProposals();

    // Limpiar los inputs
    const productInput = document.querySelector('input[placeholder="Buscar productos..."]') as HTMLInputElement;
    if (productInput) {
      productInput.value = '';
    }

    const identifierInput = document.querySelector('input[placeholder="Buscar identificadores..."]') as HTMLInputElement;
    if (identifierInput) {
      identifierInput.value = '';
    }
  }

  hideProductDropdown(): void {
    setTimeout(() => {
      this.showProductDropdown = false;
    }, 150);
  }

  hideIdentifierDropdown(): void {
    setTimeout(() => {
      this.showIdentifierDropdown = false;
    }, 150);
  }

  onProductSearchFocus(): void {
    if (this.productSearchTerm.length > 0 && this.filteredProducts.length > 0) {
      this.showProductDropdown = true;
    } else if (this.productSearchTerm.length === 0 && this.availableProducts.length > 0) {
      this.filteredProducts = [...this.availableProducts];
      this.showProductDropdown = this.filteredProducts.length > 0;
    }
  }

  onIdentifierSearchFocus(): void {
    if (this.identifierSearchTerm.length > 0 && this.filteredIdentifiers.length > 0) {
      this.showIdentifierDropdown = true;
    } else if (this.identifierSearchTerm.length === 0 && this.availableIdentifiers.length > 0) {
      this.filteredIdentifiers = [...this.availableIdentifiers];
      this.showIdentifierDropdown = this.filteredIdentifiers.length > 0;
    }
  }

  canAddProduct(): boolean {
    // Solo permitir agregar si no hay productos y hay selecciones válidas
    return this.addedProducts.length === 0 && this.selectedProduct && this.selectedIdentifier;
  }

  getAddedProductsCount(): number {
    return this.addedProducts.length;
  }

  getAddedProductsText(): string {
    const count = this.getAddedProductsCount();
    if (count === 0) return 'Ningún producto seleccionado';
    if (count === 1) return `Producto: ${this.addedProducts[0].product.name} (${this.addedProducts[0].identifier.name})`;
    return 'Error: Solo se permite un producto'; // No debería llegar aquí
  }

  // Nuevo método para verificar si ya hay un producto seleccionado
  hasProductSelected(): boolean {
    return this.addedProducts.length > 0;
  }

  // Nuevo método para obtener el producto seleccionado
  getSelectedProductInfo(): any {
    return this.addedProducts.length > 0 ? this.addedProducts[0] : null;
  }

  validateEndDate(control: any): { [key: string]: boolean } | null {
    const startDate = control.parent?.get('startDate')?.value;
    const endDate = control.value;

    if (startDate && endDate && endDate < startDate) {
      return { endDateBeforeStartDate: true };
    }
    return null;
  }

  validateStartDate(control: any): { [key: string]: boolean } | null {
    const startDate = control.value;

    if (startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to beginning of day for comparison

      const selectedDate = new Date(startDate);
      selectedDate.setHours(0, 0, 0, 0); // Reset time to beginning of day for comparison

      if (selectedDate < today) {
        return { startDateInPast: true };
      }
    }
    return null;
  }

  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Maneja el cambio en el código del plan
   */
  onPlanCodeChange(event: any): void {
    const code = event.target.value;
    const cleanCode = code.toUpperCase().replace(/[^A-Z0-9_-]/g, '');

    if (cleanCode !== code) {
      this.step4Form.patchValue({ planCode: cleanCode });
    }

    // Limpiar el resultado anterior cuando el usuario escribe
    this.codeCheckResult = null;
  }

  /**
   * Maneja el evento blur del código del plan
   */
  async onPlanCodeBlur(): Promise<void> {
    const code = this.step4Form.get('planCode')?.value;
    console.log('🔵 onPlanCodeBlur llamado, código:', code);
    if (code) {
      await this.validatePlanCodeUniqueness(code);
    }
  }

  /**
   * Valida que el código del plan sea único
   */
  private async validatePlanCodeUniqueness(code: string): Promise<void> {
    console.log('🔍 validatePlanCodeUniqueness llamado con código:', code);

    if (!code || code.length < 3) {
      console.log('⚠️ Código muy corto o vacío, saltando validación');
      return;
    }

    try {
      this.isCheckingCode = true;
      this.codeCheckResult = null;

      console.log('📡 Llamando al servicio checkCodeExists...');
      const response = await this.planService.checkCodeExists(code).toPromise();

      console.log('✅ Respuesta del servicio:', response);

      this.codeCheckResult = response;

      if (response.exists) {
        console.log('❌ El código YA EXISTE');
        // El código ya existe - marcar error
        this.step4Form.get('planCode')?.setErrors({ duplicate: true });
        this.step4Form.get('planCode')?.markAsTouched();
      } else {
        console.log('✅ El código está DISPONIBLE');
        // El código está disponible - limpiar error de duplicado
        const currentErrors = this.step4Form.get('planCode')?.errors;
        if (currentErrors && currentErrors['duplicate']) {
          delete currentErrors['duplicate'];
          this.step4Form.get('planCode')?.setErrors(
            Object.keys(currentErrors).length > 0 ? currentErrors : null
          );
        }
      }
    } catch (error: any) {
      console.error('❌ Error al verificar código:', error);
      console.error('❌ Error completo:', JSON.stringify(error, null, 2));
      // En caso de error, no bloqueamos al usuario
      this.codeCheckResult = null;
    } finally {
      this.isCheckingCode = false;
    }
  }

  /**
   * Obtiene el tipo de plan seleccionado
   */
  getSelectedPlanType(): string {
    const planType = this.step4Form.get('planType')?.value || '';

    // DEBUG: Log para verificar el valor
    console.log('🔍 getSelectedPlanType:', {
      value: planType,
      formExists: !!this.step4Form,
      controlExists: !!this.step4Form?.get('planType'),
      controlValue: this.step4Form?.get('planType')?.value,
      controlStatus: this.step4Form?.get('planType')?.status
    });

    return planType;
  }

  /**
   * Verifica si el plan es de tipo contado
   */
  isPlanTypeCash(): boolean {
    return this.getSelectedPlanType() === 'cash';
  }

  /**
   * Verifica si el plan es de tipo crédito o suscripción
   * Ambos tipos se comportan igual (financiamiento con cuotas)
   */
  isPlanTypeCredit(): boolean {
    const planType = this.getSelectedPlanType();
    return planType === 'credit' || planType === 'suscripcion';
  }

  /**
   * Maneja el cambio del toggle de estatus
   */
  onStatusToggle(event: any): void {
    const isChecked = event.target.checked;
    this.step4Form.patchValue({ status: isChecked ? 'true' : 'false' });
  }

  /**
   * Maneja el cambio del tipo de plan
   */
  onPlanTypeChange(): void {
    const planType = this.getSelectedPlanType();

    // DEBUG: Agregar logs para debugging
    console.log('🔄 onPlanTypeChange - DEBUG INFO:');
    console.log('📋 Selected Plan Type:', planType);
    console.log('📋 Current Step:', this.currentStep);
    console.log('📋 Step4Form planType value:', this.step4Form?.get('planType')?.value);

    if (planType === 'cash') {
      // Configurar automáticamente los valores para plan de contado
      console.log('💰 Configuring CASH plan...');
      this.configureCashPlan();

      // Si estamos en el paso 5 (Datos Generales) y cambiamos a contado,
      // navegar al siguiente paso visible
      if (this.currentStep === 5) {
        console.log('⏭️ Skipping step 5 for cash plan');
        this.currentStep = this.getNextStepNumber(5);
      }
    } else if (planType === 'credit' || planType === 'suscripcion') {
      // Configurar valores para plan de crédito o suscripción
      console.log(`💳 Configuring ${planType.toUpperCase()} plan...`);
      this.configureCreditPlan();
    }

    // Actualizar validaciones
    console.log('🔄 Updating step validations...');
    this.updateStepValidations();
  }

  /**
   * Configura automáticamente los valores para plan de contado
   */
  private configureCashPlan(): void {
    // Según la documentación: inicial 100%, 0 cuotas
    // Para planes de contado, multicompra se maneja automáticamente (permitido por defecto)
    // No necesitamos configurar el campo multicompra ya que está oculto

    console.log('💰 configureCashPlan - Applying cash plan configuration...');

    // DEBUG: Verificar estado actual
    console.log('📋 Current completed steps:', this.completedSteps);
    console.log('📋 Step4 form exists:', !!this.step4Form);

    // Para planes de contado, configurar automáticamente los valores del step4 si existe
    if (this.step4Form) {
      console.log('📝 Setting default values for cash plan in step4Form...');

      // Configurar pago inicial para contado (no requerido)
      this.step4Form.patchValue({
        'initialPayment': {
          'required': false,
          'calculationType': 'amount',
          'value': 0,
          'recalculateInstallments': true
        },
        'financing': {
          'minPurchaseAmount': 0,
          'maxFinanceAmount': 0
        },
        'quotaRequirements': {
          'minQuota': 0,
          'maxQuota': 0
        },
        'installments': {
          'options': '',
          'frequency': 'mensual'
        }
      });

      console.log('✅ Step4 form configured for cash plan');
    } else {
      console.log('⚠️ Step4 form not available yet');
    }

    console.log('💰 Plan de contado seleccionado - configuración automática aplicada');

    // Marcar el paso 4 como completado automáticamente para planes de contado
    if (!this.completedSteps.includes(4)) {
      this.completedSteps.push(4);
      console.log('✅ Step 4 marked as completed for cash plan');
    }

    console.log('📋 Updated completed steps:', this.completedSteps);
  }

  /**
   * Configura valores para plan de crédito o suscripción
   */
  private configureCreditPlan(): void {
    // Para planes de crédito/suscripción, multicompra debe estar en false por defecto
    this.step3Form.patchValue({
      multicompra: false
    });

    const planType = this.getSelectedPlanType();
    console.log(`Plan de ${planType} seleccionado - multicompra deshabilitada por defecto`);

    // Si es tipo "suscripción", establecer requisitos de cupo en 0
    if (planType === 'suscripcion') {
      this.step5Form?.patchValue({
        quotaRequirements: {
          minQuota: 0,
          maxQuota: 0
        }
      });
      console.log('Plan de suscripción: requisitos de cupo establecidos en 0');
    }
  }

  /**
   * Actualiza las validaciones según el tipo de plan
   */
  private updateStepValidations(): void {
    // Recalcular validaciones de pasos
    this.updateStepCompletion();
  }

  // Métodos de validación por paso
  private validateStep1(): boolean {
    console.log('🔍 Validating Step 1...');

    const form = this.step1Form;
    if (!form) {
      console.log('❌ Step 1 form is null');
      return false;
    }

    console.log('📋 Form state:', {
      valid: form.valid,
      invalid: form.invalid,
      errors: form.errors,
      status: form.status
    });

    const filterMode = form.get('filterMode')?.value;
    const selectedTags = form.get('selectedTags')?.value || [];

    console.log('📊 Step 1 values:', {
      filterMode,
      selectedTags,
      selectedTagsCount: selectedTags.length,
      filterModeControl: {
        value: form.get('filterMode')?.value,
        valid: form.get('filterMode')?.valid,
        errors: form.get('filterMode')?.errors
      },
      selectedTagsControl: {
        value: form.get('selectedTags')?.value,
        valid: form.get('selectedTags')?.valid,
        errors: form.get('selectedTags')?.errors
      }
    });

    // Las etiquetas y el modo de filtro son completamente opcionales
    // El paso 1 siempre es válido, ya que la segmentación por etiquetas es opcional
    console.log('✅ Step 1 validation passed - Tags and filter mode are completely optional');
    return true;
  }

  private validateStep2(): boolean {
    // El paso 2 (Penalidades) es completamente opcional
    // No hay campos obligatorios en este paso
    return true;
  }

  private validateStep3(): boolean {
    const form = this.step3Form;
    if (!form) return false;

    // Debe haber al menos un producto agregado
    return this.addedProducts.length > 0;
  }

  private validateStep4(): boolean {
    const form = this.step4Form;
    if (!form) return false;

    // Campos obligatorios para el paso 4 (Datos Básicos)
    const requiredFields = ['planType', 'planName', 'planCode', 'startDate'];
    const missingFields = requiredFields.filter(field => {
      const control = form.get(field);
      return !control || !control.value || (typeof control.value === 'string' && control.value.trim() === '');
    });

    console.log('🔍 Validating Step 4 - Required fields:', requiredFields);
    console.log('📊 Form values:', {
      planType: form.get('planType')?.value,
      planName: form.get('planName')?.value,
      planCode: form.get('planCode')?.value,
      startDate: form.get('startDate')?.value,
      status: form.get('status')?.value,
      priority: form.get('priority')?.value
    });
    console.log('❌ Missing fields:', missingFields);

    return missingFields.length === 0;
  }

  private validateStep5(): boolean {
    console.log('🔍 Validating Step 5...');

    if (this.getSelectedPlanType() === 'cash') {
      console.log('✅ Cash plan - Step 5 automatically valid');
      return true; // Los planes de contado no requieren configuración del paso 5
    }

    // Validar formulario básico
    if (this.step5Form.invalid) {
      console.log('❌ Step 5 form is invalid');
      console.log('Form errors:', this.step5Form.errors);

      // Log específico de errores por control
      Object.keys(this.step5Form.controls).forEach(key => {
        const control = this.step5Form.get(key);
        if (control && control.invalid) {
          console.log(`❌ ${key} errors:`, control.errors);

          // Si es un FormGroup, revisar sus controles hijos
          if (control instanceof FormGroup) {
            Object.keys(control.controls).forEach(subKey => {
              const subControl = control.get(subKey);
              if (subControl && subControl.invalid) {
                console.log(`❌ ${key}.${subKey} errors:`, subControl.errors);
              }
            });
          }
        }
      });

      return false;
    }

    // Validaciones específicas de negocio
    const initialRequired = this.step5Form.get('initialPayment.required')?.value;
    const calculationType = this.step5Form.get('initialPayment.calculationType')?.value;
    const initialValue = this.step5Form.get('initialPayment.value')?.value;
    const minPurchaseAmount = this.step5Form.get('financing.minPurchaseAmount')?.value;
    const maxFinanceAmount = this.step5Form.get('financing.maxFinanceAmount')?.value;

    console.log('📊 Form values:', {
      initialRequired,
      calculationType,
      initialValue,
      minPurchaseAmount,
      maxFinanceAmount
    });

    // Validar que los valores básicos estén presentes
    if (!minPurchaseAmount || minPurchaseAmount <= 0) {
      console.log('❌ Invalid minPurchaseAmount:', minPurchaseAmount);
      return false;
    }

    if (!maxFinanceAmount || maxFinanceAmount <= 0) {
      console.log('❌ Invalid maxFinanceAmount:', maxFinanceAmount);
      return false;
    }

    // Calcular el monto mínimo a financiar
    const minFinanceAmount = this.calculateMinFinanceAmount();
    console.log('💰 Calculated minFinanceAmount:', minFinanceAmount);

    // Validar que el monto máximo sea mayor al mínimo calculado (con tolerancia para evitar problemas de precisión)
    if (maxFinanceAmount <= minFinanceAmount + 0.01) {
      console.log('❌ maxFinanceAmount <= minFinanceAmount:', maxFinanceAmount, '<=', minFinanceAmount);
      return false;
    }

    // Validar regla de oro: Si inicial es monto absoluto, debe ser menor al monto mínimo de compra
    if (initialRequired && calculationType === 'absolute' &&
      initialValue > 0 && initialValue >= minPurchaseAmount) {
      console.log('❌ Golden rule violated - absolute initial >= minPurchaseAmount:', initialValue, '>=', minPurchaseAmount);
      return false;
    }

    // Validar que las opciones de cuotas sean válidas (Nueva lógica)
    if (!this.paymentTermsValid || this.paymentTerms.length === 0) {
      console.log('❌ No valid payment terms configured');
      return false;
    }

    console.log('✅ Step 5 validation passed');
    return true;
  }

  /**
   * Valida el paso 6 (Filtros)
   * Los filtros son opcionales, pero si se habilitan deben estar correctamente configurados
   */
  private validateStep6(): boolean {
    const form = this.step6Form;
    if (!form) return false;

    const filterType = form.get('filterType')?.value;

    // Si no se selecciona ningún tipo de filtro, es válido (filtros opcionales)
    if (!filterType) {
      return true;
    }

    // Validar configuración por persona
    if (filterType === 'person') {
      const personConfigEnabled = form.get('personConfig.enabled')?.value;
      if (personConfigEnabled) {
        const personType = form.get('personConfig.personType')?.value;
        if (!personType) {
          console.log('⚠️ Warning: Person filter enabled but no person type selected');
          return false;
        }
      }
    }

    // Validar configuración por tienda (solo para planes de crédito)
    if (filterType === 'store' && this.isPlanTypeCredit()) {
      const storeConfigEnabled = form.get('storeConfig.enabled')?.value;
      if (storeConfigEnabled) {
        const selectedStores = form.get('storeConfig.selectedStores')?.value || [];
        const filterType = form.get('storeConfig.filterType')?.value;

        if (!filterType) {
          console.log('⚠️ Warning: Store filter enabled but no filter type selected');
          return false;
        }

        if (selectedStores.length === 0) {
          console.log('⚠️ Warning: Store filter enabled but no stores selected');
          return false;
        }
      }
    }

    return true;
  }

  private validateStep7(): boolean {
    // El paso 7 (Otros) es completamente opcional
    // Todas las configuraciones son toggles opcionales que pueden permanecer en false
    return true;
  }

  private validateStep8(): boolean {
    const form = this.step8Form;
    if (!form) return false;

    // TODO: Implementar validaciones específicas para finalizar
    return form.valid;
  }

  // Método para actualizar el estado de completado de los pasos
  private updateStepCompletion(): void {
    // Recalcular qué pasos están completados
    for (let i = 1; i <= this.totalSteps; i++) {
      if (this.isStepValidForCompletion(i) && !this.completedSteps.includes(i)) {
        this.completedSteps.push(i);
      }
    }
  }

  private isStepValidForCompletion(stepNumber: number): boolean {
    switch (stepNumber) {
      case 1:
        return this.validateStep1();
      case 2:
        return this.validateStep2();
      case 3:
        return this.validateStep3();
      case 4:
        return this.validateStep4();
      case 5:
        // Si es plan de contado, el paso 5 (Datos Generales) se considera completado automáticamente
        if (this.getSelectedPlanType() === 'cash') {
          return true;
        }
        return this.validateStep5();
      case 6:
        return this.validateStep6();
      case 7:
        return this.validateStep7();
      case 8:
        return this.validateStep8();
      default:
        return false;
    }
  }

  // MÉTODOS DE CONFIGURACIÓN DE NAVEGACIÓN

  /**
   * Habilita la navegación secuencial (modo producción)
   */
  enableSequentialNavigation(): void {
    this.enforceSequentialNavigation = true;
    console.log('🔒 Navegación secuencial habilitada - Solo pasos completados');
  }

  /**
   * Habilita la navegación libre (modo desarrollo/pruebas)
   */
  enableFreeNavigation(): void {
    this.enforceSequentialNavigation = false;
    console.log('🔓 Navegación libre habilitada - Todos los pasos accesibles');
  }

  /**
   * Alterna entre modos de navegación
   */
  toggleNavigationMode(): void {
    this.enforceSequentialNavigation = !this.enforceSequentialNavigation;
    const mode = this.enforceSequentialNavigation ? 'Secuencial 🔒' : 'Libre 🔓';
    console.log(`🔄 Modo de navegación cambiado a: ${mode}`);
  }

  /**
   * Obtiene el estado actual de navegación
   */
  getNavigationMode(): string {
    return this.enforceSequentialNavigation ? 'sequential' : 'free';
  }

  /**
   * Obtiene la descripción del modo actual
   */
  getNavigationModeDescription(): string {
    return this.enforceSequentialNavigation ?
      'Navegación secuencial activa. Debe completar cada paso para continuar.' :
      'Navegación libre activa. Puede navegar entre pasos sin validaciones.';
  }

  // Métodos específicos para el Paso 4: Datos Generales

  /**
   * Calcula el monto mínimo de compra basado en el monto mínimo a financiar y el pago inicial
   */
  calculateMinPurchaseAmount(): number {
    const minFinanceAmount = this.step5Form.get('financing.minPurchaseAmount')?.value || 0;
    const initialRequired = this.step5Form.get('initialPayment.required')?.value;
    const calculationType = this.step5Form.get('initialPayment.calculationType')?.value;
    const initialValue = this.step5Form.get('initialPayment.value')?.value || 0;

    if (!initialRequired || initialValue === 0) {
      return minFinanceAmount;
    }

    if (calculationType === 'percentage') {
      // Si es porcentaje: Monto de compra = Monto a financiar / (1 - porcentaje/100)
      const percentage = initialValue / 100;
      return minFinanceAmount / (1 - percentage);
    } else {
      // Si es monto absoluto: Monto de compra = Monto a financiar + Valor inicial
      return minFinanceAmount + initialValue;
    }
  }

  /**
   * Calcula el monto mínimo a financiar basado en el monto de compra y el pago inicial
   */
  calculateMinFinanceAmount(): number {
    const minPurchaseAmount = this.step5Form.get('financing.minPurchaseAmount')?.value || 0;
    const initialRequired = this.step5Form.get('initialPayment.required')?.value;
    const calculationType = this.step5Form.get('initialPayment.calculationType')?.value;
    const initialValue = this.step5Form.get('initialPayment.value')?.value || 0;

    if (!initialRequired || initialValue === 0) {
      return minPurchaseAmount;
    }

    if (calculationType === 'percentage') {
      // Si es porcentaje: Monto a financiar = Monto de compra - (Monto de compra × porcentaje/100)
      const percentage = initialValue / 100;
      return minPurchaseAmount - (minPurchaseAmount * percentage);
    } else {
      // Si es monto absoluto: Monto a financiar = Monto de compra - Valor inicial
      return Math.max(0, minPurchaseAmount - initialValue);
    }
  }

  /**
   * Método llamado cuando cambian los campos de financiamiento para recalcular valores
   */
  onFinancingFieldChange(): void {
    // Este método se ejecuta cuando el usuario cambia valores en los campos de financiamiento
    // Permite que Angular actualice los valores calculados en tiempo real
    // No necesita lógica adicional ya que el cálculo se hace en calculateMinFinanceAmount()
    
    // Actualizar validadores del campo 'value' según el estado de 'required'
    const initialRequired = this.step5Form.get('initialPayment.required')?.value;
    const valueControl = this.step5Form.get('initialPayment.value');
    
    if (valueControl) {
      if (initialRequired) {
        // Si el pago inicial es requerido, hacer el campo 'value' obligatorio
        valueControl.setValidators([Validators.required, Validators.min(0)]);
      } else {
        // Si el pago inicial no es requerido, el campo 'value' es opcional
        valueControl.setValidators([Validators.min(0)]);
      }
      valueControl.updateValueAndValidity();
    }
  }

  /**
   * Generar payment_terms desde los datos del formulario step5 (para wizard de creación)
   */
  generatePaymentTermsFromBuilder(): any[] {
    console.log('🔄 Generando payment_terms desde builder...');

    const planCode = this.step4Form?.get('planCode')?.value || '000000';

    if (this.paymentTerms.length === 0) {
      console.log('⚠️ No hay términos de pago configurados, retornando array vacío');
      return [];
    }

    // Generar payment_terms para cada opción configurada
    const paymentTermsDB = this.paymentTerms.map((term) => {
      const code = `${planCode}_${term.installments}`;
      const frequencyDB = this.mapFrequencyToDBFormat(term.frequency);
      const name = term.frequency === 'dias_continuos'
        ? `${term.installments} cuotas pagadas cada ${term.frequencyDays} días`
        : `${term.installments} cuotas pagadas ${frequencyDB}`;

      return {
        code: code,
        frequency: {
          type: frequencyDB,
          ...(term.frequency === 'dias_continuos' && { number: term.frequencyDays })
        },
        name: name,
        order: term.order,
        installments: term.installments
      };
    });

    console.log('✅ Payment terms generados:', paymentTermsDB);
    return paymentTermsDB;
  }

  // Métodos para el PaymentTermsBuilder
  onPaymentTermsChanged(terms: PaymentTerm[]): void {
    this.paymentTerms = terms;
    console.log('🔄 Payment terms updated:', this.paymentTerms);
  }

  onPaymentTermsValidityChanged(isValid: boolean): void {
    this.paymentTermsValid = isValid;
    console.log('🔄 Payment terms validity updated:', this.paymentTermsValid);

    // Forzar validación del formulario padre para actualizar estado del botón siguiente
    // Forzar validación del formulario padre para actualizar estado del botón siguiente
    this.step5Form.updateValueAndValidity();
  }

  private mapFrequencyToDBFormat(frequency: string): string {
    switch (frequency) {
      case 'semanal':
        return 'semanal';
      case 'quincenal':
        return 'quincenal';
      case 'mensual':
        return 'mensual';
      case 'dias_continuos':
        return 'dias_continuos';
      // Retrocompatibilidad con valores en inglés
      case 'weekly':
        return 'semanal';
      case 'biweekly':
        return 'quincenal';
      default:
        return 'mensual';
    }
  }



  /**
   * Copia el JSON actual del plan al portapapeles
   * TEMPORAL: Solo para debugging
   */
  copyJSONToClipboard(): void {
    try {
      const jsonString = this.getCurrentPlanJSONString();
      navigator.clipboard.writeText(jsonString).then(() => {
        console.log('✅ JSON copiado al portapapeles');
        // Aquí podrías agregar una notificación toast si tienes un servicio de notificaciones
      }).catch(err => {
        console.error('❌ Error copiando JSON al portapapeles:', err);
        // Fallback: crear un elemento temporal para copiar
        this.fallbackCopyToClipboard(jsonString);
      });
    } catch (error) {
      console.error('❌ Error preparando JSON para copiar:', error);
    }
  }

  /**
   * Método fallback para copiar al portapapeles en navegadores que no soportan navigator.clipboard
   */
  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      console.log('✅ JSON copiado al portapapeles (fallback)');
    } catch (err) {
      console.error('❌ Error en método fallback para copiar:', err);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  // MÉTODOS TEMPORALES PARA DEBUGGING - JSON EN TIEMPO REAL
  /**
   * Genera el JSON interno del wizard para visualización en tiempo real
   * TEMPORAL: Muestra cómo se van llenando los datos paso a paso
   */
  getWizardInternalJSON(): any {
    return {
      // Metadatos del wizard
      _wizard: {
        currentStep: this.currentStep,
        completedSteps: this.completedSteps,
        enforceSequentialNavigation: this.enforceSequentialNavigation,
        totalSteps: this.totalSteps,
        selectedPlanType: this.getSelectedPlanType()
      },

      // Paso 1: Etiquetas/Códigos Dinámicos
      step1_tags: {
        filterMode: this.step1Form?.get('filterMode')?.value || null,
        selectedTags: this.step1Form?.get('selectedTags')?.value || [],
        isValid: this.validateStep1()
      },

      // Paso 2: Productos
      step2_products: {
        selectedProducts: this.step2Form?.get('selectedProducts')?.value || [],
        addedProductsCount: this.addedProducts.length,
        isValid: this.validateStep2()
      },

      // Paso 3: Datos Básicos
      step3_basic: {
        planType: this.step3Form?.get('planType')?.value || '',
        planName: this.step3Form?.get('planName')?.value || '',
        planCode: this.step3Form?.get('planCode')?.value || '',
        description: this.step3Form?.get('description')?.value || '',
        status: this.step3Form?.get('status')?.value || 'false',
        priority: this.step3Form?.get('priority')?.value || 1,
        startDate: this.step3Form?.get('startDate')?.value || '',
        endDate: this.step3Form?.get('endDate')?.value || '',
        isValid: this.validateStep3()
      },

      // Paso 4: Configuración de Crédito (solo para planes de crédito)
      step4_credit: this.getSelectedPlanType() === 'credit' ? {
        initialPayment: {
          required: this.step4Form?.get('initialPayment.required')?.value || false,
          type: this.step4Form?.get('initialPayment.type')?.value || 'percentage',
          amount: this.step4Form?.get('initialPayment.amount')?.value || 0,
          recalculate: this.step4Form?.get('initialPayment.recalculate')?.value || false
        },
        paymentTerms: this.paymentTerms,
        goldenRule: {
          enabled: this.step4Form?.get('goldenRule.enabled')?.value || false,
          absoluteInitialPayment: this.step4Form?.get('goldenRule.absoluteInitialPayment')?.value || 0
        },
        calculatedFields: {
          minFinanceAmount: this.calculateMinFinanceAmount()
        },
        isValid: this.validateStep4()
      } : null,

      // Paso 5: Configuración Adicional
      step5_additional: {
        visibility: this.step5Form?.get('visibility')?.value || 'PUBLIC',
        clientView: this.step5Form?.get('clientView')?.value || false,
        generatePageres: this.step5Form?.get('generatePageres')?.value || false,
        generatePageresFisico: this.step5Form?.get('generatePageresFisico')?.value || false,
        weekdayOnly: this.step5Form?.get('weekdayOnly')?.value || false,
        multipleInvoices: {
          enabled: this.step5Form?.get('multipleInvoices.enabled')?.value || false,
          count: this.step5Form?.get('multipleInvoices.count')?.value || 0
        },
        priority: this.step5Form?.get('priority')?.value || 0,
        icon: this.step3Form?.get('icon')?.value || 'fas fa-star',
        isValid: this.validateStep5()
      },

      // Paso 6: Configuración de Proveedor
      step6_provider: {
        selectedProvider: this.step6Form?.get('provider')?.value || null,
        isValid: this.validateStep6()
      },

      // Paso 7: Resumen
      step7_summary: {
        isComplete: this.validateStep7()
      }
    };
  }

  /**
   * Genera el JSON en formato de base de datos para envío
   * CORREGIDO: Estructura exacta de producción
   */
  getDatabaseFormatJSON(): any {
    const planType = this.getSelectedPlanType();
    const isCredit = planType === 'credit' || planType === 'suscripcion';

    // Determinar el tipo de params.type según el planType
    const paramsType = planType === 'suscripcion' ? 'SUSCRIPCION' : (planType === 'credit' ? 'CREDITO' : 'CONTADO');

    // DEBUG: Agregar logs para debugging
    console.log('🔍 getDatabaseFormatJSON - DEBUG INFO:');
    console.log('📋 Plan Type:', planType);
    console.log('📋 Params Type (DB):', paramsType);
    console.log('📋 Is Credit:', isCredit);
    console.log('📋 Is Cash:', !isCredit && planType === 'cash');
    console.log('📋 Step4Form planType value:', this.step4Form?.get('planType')?.value);

    // Preparar códigos dinámicos (etiquetas)
    let filterMode = this.step1Form?.get('filterMode')?.value;
    const selectedTags = this.step1Form?.get('selectedTags')?.value || [];

    // Si hay etiquetas seleccionadas pero no se eligió modo, usar whitelist por defecto
    if (!filterMode && selectedTags.length > 0) {
      filterMode = 'whitelist';
    }

    let dinamicCode = null;
    if (filterMode && selectedTags.length > 0) {
      dinamicCode = {
        person: {
          type: filterMode === 'whitelist' ? 'white_list' : 'black_list',
          list: selectedTags
        }
      };
    }

    // Obtener datos básicos del STEP 4 (Datos Básicos)
    const planName = this.step4Form?.get('planName')?.value || '';
    const planCode = this.step4Form?.get('planCode')?.value || '';
    const planDescription = this.step4Form?.get('description')?.value || '';
    const planIcon = this.step4Form?.get('icon')?.value || 'fas fa-star';
    const planStatus = this.step4Form?.get('status')?.value === 'true';

    // Preparar el objeto principal
    const planData: any = {
      name: planName,
      code: planCode,
      description: planDescription,
      status: planStatus,
      state: planStatus ? 'ACTIVE' : 'INACTIVE',
      visibility: 'PUBLIC',
      date: new Date(),
      init_date: new Date(), // Campo requerido adicional

      // 👤 CAMPO CREATED_BY - Usuario creador
      created_by: {
        user: this.authService.sessionUser?._id || null,
        date: new Date()
      },

      history: [], // Array vacío inicial
      version_change: [], // Array vacío inicial
      version: {
        code: `${planCode}_V1`,
        name: `${planName} - Versión 1`,
        number: 1,
        description: planDescription,
        date_init_version: new Date(),
        internal_code: 'v1' // Campo requerido adicional
      },
      params: {
        type: paramsType,
        // Configurar campos específicos según tipo de plan
        // Para CONTADO: todos los montos en 0, para CRÉDITO/SUSCRIPCIÓN: usar valores del formulario
        min_amount: isCredit ? (this.step5Form?.get('financing.minPurchaseAmount')?.value || 0) : 0,
        max_amount_base: isCredit ? (this.step5Form?.get('financing.maxFinanceAmount')?.value || 0) : 0,
        min_amount_base: isCredit ? (this.calculateMinFinanceAmount() || 0) : 0,
        // Para planes de SUSCRIPCIÓN, los requisitos de cupo siempre son 0 (no aplican)
        current_quota_min: (isCredit && planType !== 'suscripcion') ? (this.step5Form?.get('quotaRequirements.minQuota')?.value || 0) : 0,
        current_quota_max: (isCredit && planType !== 'suscripcion') ? (this.step5Form?.get('quotaRequirements.maxQuota')?.value || 0) : 0,
        init_date: this.step4Form?.get('startDate')?.value || new Date().toISOString().split('T')[0],
        client_view: this.step7Form?.get('electronicPromissoryNote')?.value || false,
        generate_pagares: this.step7Form?.get('electronicPromissoryNote')?.value || false,
        generate_pagares_fisico: this.step7Form?.get('physicalPromissoryNote')?.value || false,
        priority: this.step4Form?.get('priority')?.value ?? 0,
        icon: planIcon,
        multiple_invoices: {
          status: this.step4Form?.get('multicompra')?.value || false,
          count: this.step4Form?.get('multicompra')?.value ? 0 : 0
        },
        filters: {
          general: {
            person: {
              audience: this.step6Form?.get('personConfig.personType')?.value?.toUpperCase() || 'NATURAL'
            }
          },
          ...(dinamicCode && { dinamic_code: dinamicCode })
        },
        provider: null,
        product_type: this.addedProducts.length > 0 ? this.addedProducts[0].identifier.code : '',
        weekday: this.step7Form?.get('allowWeekendsAndHolidays')?.value,
        client_visible: this.step7Form?.get('clientVisible')?.value ?? true, // Visibilidad del plan al cliente
        // Incluir propuesta de seguros si existe
        insurance: (() => {
          console.log('🔍 EVALUANDO insurance (CREACIÓN):');
          console.log('  - addedProducts.length:', this.addedProducts.length);
          console.log('  - addedProducts[0]:', this.addedProducts[0]);
          console.log('  - addedProducts[0]?.insuranceProposal:', this.addedProducts[0]?.insuranceProposal);
          console.log('  - addedProducts[0]?.identifier?.insurance_product:', this.addedProducts[0]?.identifier?.insurance_product);
          console.log('  - selectedProposal:', this.selectedProposal);
          console.log('  - selectedProduct:', this.selectedProduct);
          console.log('  - selectedProduct?.insurance:', this.selectedProduct?.insurance);

          // Obtener el código del plan (ID de la propuesta), nombre y prima
          let planCode = null;
          let proposalName = null;
          let mtPrima = null;

          if (this.addedProducts.length > 0 && this.addedProducts[0].insuranceProposal) {
            const proposal = this.addedProducts[0].insuranceProposal;
            // Si es un objeto con code, name y mtPrima
            if (typeof proposal === 'object' && proposal.code) {
              planCode = proposal.code;
              mtPrima = proposal.mtPrima;
              // Construir proposal_name con formato: "Nombre - Prima"
              proposalName = proposal.name && mtPrima ? `${proposal.name} - ${mtPrima}` : proposal.name;
            } else {
              // Si es solo un string (retrocompatibilidad)
              planCode = proposal;
            }
          } else if (this.selectedProposal) {
            planCode = this.selectedProposal;
            // Buscar el nombre y prima en la lista de propuestas
            const foundProposal = this.insuranceProposals.find(p => p.code === this.selectedProposal);
            if (foundProposal) {
              mtPrima = foundProposal.mtPrima;
              proposalName = foundProposal.name && mtPrima ? `${foundProposal.name} - ${mtPrima}` : foundProposal.name;
            }
          }

          // Obtener el código de la aseguradora del producto seleccionado
          const brokerageCode = this.addedProducts.length > 0 && this.addedProducts[0].product?.insurance?.insurer_code ?
            this.addedProducts[0].product.insurance.insurer_code :
            (this.selectedProduct?.insurance?.insurer_code || null);

          // Obtener el insurance_product del identificador seleccionado
          const insuranceProductCode = this.addedProducts.length > 0 && this.addedProducts[0].identifier?.insurance_product ?
            this.addedProducts[0].identifier.insurance_product :
            (this.selectedIdentifier?.insurance_product || null);

          console.log('  - VALOR FINAL para insurance.plan:', planCode);
          console.log('  - VALOR FINAL para insurance.proposal_name:', proposalName);
          console.log('  - VALOR FINAL para insurance.mtPrima:', mtPrima);
          console.log('  - VALOR FINAL para insurance.product:', insuranceProductCode);
          console.log('  - VALOR FINAL para insurance.brokerage:', brokerageCode);

          return {
            plan: planCode,
            proposal_name: proposalName,
            mtPrima: mtPrima,
            product: insuranceProductCode,
            brokerage: brokerageCode
          };
        })(),
      },
      // Generar payment_terms desde el formulario
      payment_terms: this.generatePaymentTermsFromBuilder()
    };

    // NUEVO: Agregar filtros de tienda si están habilitados (solo para planes de crédito)
    if (isCredit && this.areStoreFiltersEnabled()) {
      const storeFilterType = this.step6Form?.get('storeConfig.filterType')?.value || '';
      const selectedStores = this.step6Form?.get('storeConfig.selectedStores')?.value || [];

      console.log('🏪 Agregando filtros de tienda al crear:', {
        enabled: this.areStoreFiltersEnabled(),
        filterType: storeFilterType,
        selectedStores: selectedStores,
        storeIds: selectedStores.map((store: any) => store.id)
      });

      if (selectedStores.length > 0) {
        planData.params.filters.general.invoices = {
          shop: {
            type: storeFilterType, // Usar directamente (ya viene como 'white_list' o 'black_list')
            list: selectedStores.map((store: any) => store.id || store.code || store._id).filter(Boolean)
          }
        };
      }
    }

    // ── multicomercio: a que comercios aplica el plan ─────────────────────────
    // ⚠️ Se MEZCLA, no se asigna: el bloque de tiendas de arriba REEMPLAZA
    // `general.invoices` entero, asi que escribir el comercio antes lo borraria
    // sin avisar. Por eso va despues y con spread.
    //
    // Si no se eligio ninguno NO se escribe la clave: un plan sin la clave aplica
    // a todos los comercios, que es la regla y ademas es como estan los 56 planes
    // que ya existen.
    const comerciosDelPlan = this.comerciosElegidos;
    if (comerciosDelPlan.length > 0) {
      planData.params.filters.general.invoices = {
        ...(planData.params.filters.general.invoices || {}),
        merchant: {
          type: 'white_list',
          list: comerciosDelPlan.map((c: any) => c.id || c.code).filter(Boolean)
        }
      };
    }

    // NUEVO: Agregar filtros de productos del catálogo si están habilitados
    const productConfigEnabled = this.step6Form?.get('productConfig.enabled')?.value || false;
    const productFilterType = this.step6Form?.get('productConfig.filterType')?.value || '';
    const productMatchMode = this.step6Form?.get('productConfig.matchMode')?.value || 'any';
    const productAllowOthers = this.step6Form?.get('productConfig.allowOthers')?.value !== false;
    const selectedCatalogProducts = this.step6Form?.get('productConfig.selectedProducts')?.value || [];

    if (productConfigEnabled && selectedCatalogProducts.length > 0 && productFilterType) {
      planData.params.filters.product_filter = {
        type: productFilterType,
        match_mode: productMatchMode,
        allow_others: productAllowOthers,
        list: selectedCatalogProducts.map((p: any) => p.code).filter(Boolean)
      };
    }

    // Agregar configuración de pago inicial desde formulario
    if (isCredit && this.step5Form?.get('initialPayment.required')?.value) {
      // Para CRÉDITO: usar valores del formulario
      planData.initial = {
        status: true,
        type: this.step5Form?.get('initialPayment.calculationType')?.value || 'percentage',
        amount: this.step5Form?.get('initialPayment.value')?.value || 0,
        recalculate: this.step5Form?.get('initialPayment.recalculateInstallments')?.value || false
      };

      console.log('💳 CRÉDITO - Initial config from form:', planData.initial);
    } else if (isCredit) {
      // Para CRÉDITO sin inicial requerida
      planData.initial = {
        status: false,
        type: 'percentage',
        amount: 0,
        recalculate: false
      };

      console.log('💳 CRÉDITO - Initial config (no initial required):', planData.initial);
    } else {
      // Para CONTADO: pago único del 100%
      planData.initial = {
        status: true,
        type: 'percentage',
        recalculate: false,
        amount: 100
      };

      console.log('💰 CONTADO - Initial config (100% upfront):', planData.initial);
    }

    console.log('📄 Final planData.initial:', planData.initial);
    console.log('📄 Final planData.params.type:', planData.params.type);
    console.log('📄 Final planData.payment_terms:', planData.payment_terms);

    console.log('GEMINI DEBUG (getDatabaseFormatJSON CREADOR): allowWeekendsAndHolidays form value =', this.step6Form?.get('allowWeekendsAndHolidays')?.value);
    console.log('GEMINI DEBUG (getDatabaseFormatJSON CREADOR): weekday sent to backend =', this.step6Form?.get('allowWeekendsAndHolidays')?.value);

    return planData;
  }

  /**
   * Obtiene el JSON del plan como string formateado para mostrar en la UI
   * Muestra únicamente el formato que se enviará a la base de datos
   */
  getCurrentPlanJSONString(): string {
    console.log('📄 getCurrentPlanJSONString - Generating JSON string...');

    try {
      const databaseData = this.getDatabaseFormatJSON();
      const jsonString = JSON.stringify(databaseData, null, 2);

      console.log('✅ JSON generated successfully');
      console.log('📊 JSON length:', jsonString.length);

      return jsonString;
    } catch (error) {
      console.error('❌ Error generating JSON:', error);
      return `Error generando JSON: ${error}`;
    }
  }

  // MÉTODOS HELPER PARA FILTROS SIMPLIFICADOS

  /**
   * Verifica si se debe mostrar la configuración de tienda
   */
  shouldShowStoreFilters(): boolean {
    return this.isPlanTypeCredit();
  }

  /**
   * Carga las tiendas disponibles del sistema
   */
  async loadAvailableStores(): Promise<void> {
    this.storesLoading = true;
    try {
      console.log('🏪 Cargando tiendas del sistema...');
      const response = await this.userService.getshops();
      console.log('🏪 Respuesta del API getshops():', response);

      let storesData: any[] = [];

      // Manejar diferentes estructuras de respuesta
      if (response) {
        if (response.res && Array.isArray(response.res)) {
          // Formato correcto: { res: [...] } - usado en otros componentes
          storesData = response.res;
          console.log('🏪 Formato con res detectado (correcto)');
        } else if (response.success && Array.isArray(response.data)) {
          // Formato: { success: true, data: [...] }
          storesData = response.data;
          console.log('🏪 Formato con success/data detectado');
        } else if (Array.isArray(response.data)) {
          // Formato: { data: [...] } sin success
          storesData = response.data;
          console.log('🏪 Formato solo con data detectado');
        } else if (Array.isArray(response)) {
          // Formato: directamente un array
          storesData = response;
          console.log('🏪 Formato array directo detectado');
        } else if (response.resp && Array.isArray(response.resp)) {
          // Formato: { resp: [...] } (común en otros endpoints)
          storesData = response.resp;
          console.log('🏪 Formato con resp detectado');
        }
      }

      if (storesData.length > 0) {
        console.log('🏪 Datos de tiendas encontrados:', storesData.length, 'tiendas');
        console.log('🏪 Primera tienda de ejemplo:', storesData[0]);

        // Mapear la respuesta del API al formato esperado por el componente
        this.availableStores = storesData.map((shop: any) => ({
          id: shop.code || shop.id || shop.shop_code,
          name: shop.name || shop.shop_name || shop.title || 'Sin nombre',
          category: shop.category || shop.type || 'Principal',
          city: shop.city || shop.location || shop.address || 'No especificada'
        })).filter(store => store.id); // Solo tiendas con ID válido

        console.log('🏪 Tiendas mapeadas:', this.availableStores.length);
        console.log('🏪 Primera tienda mapeada:', this.availableStores[0]);
      } else {
        console.warn('🏪 No se encontraron tiendas en la respuesta');
        console.warn('🏪 Estructura de respuesta:', {
          hasResponse: !!response,
          hasRes: !!response?.res,
          hasSuccess: response?.success,
          hasData: !!response?.data,
          hasResp: !!response?.resp,
          isResArray: Array.isArray(response?.res),
          isDataArray: Array.isArray(response?.data),
          isRespArray: Array.isArray(response?.resp),
          isDirectArray: Array.isArray(response),
          responseKeys: response ? Object.keys(response) : 'No response',
          responseType: typeof response
        });
        this.availableStores = [];
      }

      this.filteredStores = [...this.availableStores];
    } catch (error) {
      console.error('🏪 Error loading stores:', error);
      this.availableStores = [];
      this.filteredStores = [];
    } finally {
      this.storesLoading = false;
    }
  }

  /**
   * Filtra tiendas según término de búsqueda
   */
  filterStores(): void {
    const searchTerm = this.storeSearchTerm.toLowerCase().trim();
    if (!searchTerm) {
      this.filteredStores = [...this.availableStores];
    } else {
      this.filteredStores = this.availableStores.filter(store =>
        store.name.toLowerCase().includes(searchTerm) ||
        store.category.toLowerCase().includes(searchTerm) ||
        store.city.toLowerCase().includes(searchTerm)
      );
    }
  }

  /**
   * Selecciona una tienda
   */
  selectStore(store: any): void {
    const currentStores = this.step6Form?.get('storeConfig.selectedStores')?.value || [];
    if (!currentStores.find((s: any) => s.id === store.id)) {
      const updatedStores = [...currentStores, store];
      this.step6Form?.get('storeConfig.selectedStores')?.setValue(updatedStores);
    }
    this.storeSearchTerm = '';
    this.showStoreDropdown = false;
    this.filterStores();
  }

  /**
   * Remueve una tienda seleccionada
   */
  removeStore(storeToRemove: any): void {
    const currentStores = this.step6Form?.get('storeConfig.selectedStores')?.value || [];
    const updatedStores = currentStores.filter((store: any) => store.id !== storeToRemove.id);
    this.step6Form?.get('storeConfig.selectedStores')?.setValue(updatedStores);
  }

  // MÉTODOS FALTANTES PARA EL HTML

  /**
   * Verifica si los filtros de persona están habilitados
   */
  arePersonFiltersEnabled(): boolean {
    const filterType = this.step6Form?.get('filterType')?.value;
    const personEnabled = this.step6Form?.get('personConfig.enabled')?.value;
    return filterType === 'person' && personEnabled;
  }

  /**
   * Verifica si los filtros de tienda están habilitados
   */
  // ── multicomercio: comercios a los que aplica el plan ──────────────────────
  // No tiene interruptor de "habilitado" ni lista blanca/negra a proposito. La
  // pregunta del negocio es una sola —"¿a que comercios aplica?"— y la respuesta
  // por omision es "a todos". Un interruptor mas seria una forma mas de dejarlo
  // a medias: encendido y sin comercios adentro, que no significa nada.

  cargarComercios = async (): Promise<void> => {
    this.comerciosCargando = true;
    try {
      const rep: any = await this.merchantService.getMerchants({ status: true });
      this.comerciosDisponibles = (rep?.merchants || []).map((c: any) => ({
        id: c._id,
        code: c.code,
        name: c.name || c.business_name || c.code,
        state: c.state
      }));
      this.comerciosFiltrados = [...this.comerciosDisponibles];
    } catch (error) {
      // Que no se puedan listar los comercios NO bloquea la creacion del plan:
      // sin seleccionar ninguno, el plan aplica a todos, que es el valor por
      // omision de todas formas.
      console.error('No se pudieron cargar los comercios', error);
      this.comerciosDisponibles = [];
      this.comerciosFiltrados = [];
    } finally {
      this.comerciosCargando = false;
    }
  };

  /**
   * Cierra la lista al salir del campo.
   *
   * 🔴 Los 150 ms no son un capricho: el `blur` del input ocurre ANTES que el
   * `click` del elemento de la lista. Si se cierra en el acto, la opción
   * desaparece antes de que el click llegue y no se puede elegir nada.
   */
  cerrarListaComercios(): void {
    setTimeout(() => { this.mostrarListaComercios = false; }, 150);
  }

  filtrarComercios(): void {
    const t = (this.comercioBusqueda || '').toLowerCase().trim();
    this.comerciosFiltrados = !t
      ? [...this.comerciosDisponibles]
      : this.comerciosDisponibles.filter(c =>
          (c.name || '').toLowerCase().includes(t) ||
          (c.code || '').toLowerCase().includes(t));
  }

  get comerciosElegidos(): any[] {
    return this.step6Form?.get('merchantConfig.selectedMerchants')?.value || [];
  }

  elegirComercio(comercio: any): void {
    const actuales = this.comerciosElegidos;
    if (!actuales.find((c: any) => c.id === comercio.id)) {
      this.step6Form.get('merchantConfig.selectedMerchants')?.setValue([...actuales, comercio]);
    }
    this.comercioBusqueda = '';
    this.mostrarListaComercios = false;
    this.filtrarComercios();
  }

  quitarComercio(comercio: any): void {
    this.step6Form.get('merchantConfig.selectedMerchants')?.setValue(
      this.comerciosElegidos.filter((c: any) => c.id !== comercio.id));
  }

  areStoreFiltersEnabled(): boolean {
    const filterType = this.step6Form?.get('filterType')?.value;
    const storeEnabled = this.step6Form?.get('storeConfig.enabled')?.value;
    return filterType === 'store' && storeEnabled && this.isPlanTypeCredit();
  }

  /**
   * Obtiene un resumen de la configuración de filtros
   */
  getFiltersConfigSummary(): string {
    const filterType = this.step6Form?.get('filterType')?.value;

    if (!filterType) {
      return 'Sin filtros configurados';
    }

    if (filterType === 'person' && this.arePersonFiltersEnabled()) {
      const personType = this.step6Form?.get('personConfig.personType')?.value;
      return `Filtro por persona: ${personType === 'natural' ? 'Natural' : 'Jurídica'}`;
    }

    if (filterType === 'store' && this.areStoreFiltersEnabled()) {
      const storeFilterType = this.step6Form?.get('storeConfig.filterType')?.value;
      const selectedStores = this.step6Form?.get('storeConfig.selectedStores')?.value || [];
      return `Filtro por tienda: ${storeFilterType} con ${selectedStores.length} tienda(s)`;
    }

    return 'Filtros parcialmente configurados';
  }

  /**
   * Alterna los filtros de persona
   */
  togglePersonFilters(enabled: boolean): void {
    this.step6Form?.get('personConfig.enabled')?.setValue(enabled);
    if (enabled) {
      this.step6Form?.get('filterType')?.setValue('person');
    } else if (this.step6Form?.get('filterType')?.value === 'person') {
      this.step6Form?.get('filterType')?.setValue('');
    }
  }

  /**
   * Alterna los filtros de tienda
   */
  toggleStoreFilters(enabled: boolean): void {
    this.step6Form?.get('storeConfig.enabled')?.setValue(enabled);
    if (enabled) {
      this.step6Form?.get('filterType')?.setValue('store');
    } else if (this.step6Form?.get('filterType')?.value === 'store') {
      this.step6Form?.get('filterType')?.setValue('');
    }
  }

  /**
   * Filtra ubicaciones (método faltante)
   */
  filterLocations(): void {
    // TODO: Implementar filtrado de ubicaciones si es necesario
    console.log('filterLocations called');
  }

  /**
   * Selecciona una ubicación (método faltante)
   */
  selectLocation(location: any): void {
    // TODO: Implementar selección de ubicación si es necesario
    console.log('selectLocation called with:', location);
  }

  /**
   * Remueve una ubicación (método faltante)
   */
  removeLocation(location: any): void {
    // TODO: Implementar remoción de ubicación si es necesario
    console.log('removeLocation called with:', location);
  }

  /**
   * Obtiene el número de filtros activos
   */
  getActiveFiltersCount(): number {
    const personFilters = this.arePersonFiltersEnabled() ? 1 : 0;
    const storeFilters = this.areStoreFiltersEnabled() ? 1 : 0;
    return personFilters + storeFilters;
  }

  /**
   * Maneja el evento focus en el campo de búsqueda de tiendas
   */
  onStoreSearchFocus(): void {
    this.showStoreDropdown = true;
    this.filterStores();
  }

  /**
   * Oculta el dropdown de tiendas
   */
  hideStoreDropdown(): void {
    setTimeout(() => {
      this.showStoreDropdown = false;
    }, 200);
  }

  // Métodos para filtros de productos del catálogo
  async searchCatalogProducts(): Promise<void> {
    if (!this.catalogProductSearchTerm || this.catalogProductSearchTerm.trim().length < 2) {
      this.filteredCatalogProducts = [];
      return;
    }
    this.catalogProductsLoading = true;
    try {
      const response: any = await this.userService.searchCatalogProducts(this.catalogProductSearchTerm.trim());
      this.filteredCatalogProducts = (response?.res || []).map((p: any) => ({
        code: p.code,
        name: p.name,
        brand: p.brand || '',
        category: p.category || '',
        ean_upc: p.ean_upc || '',
        total_purchases: p.stats?.total_purchases || 0
      }));
    } catch (error) {
      console.error('Error buscando productos del catálogo:', error);
      this.filteredCatalogProducts = [];
    } finally {
      this.catalogProductsLoading = false;
    }
  }

  selectCatalogProduct(product: any): void {
    const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
    if (!selectedProducts.find((p: any) => p.code === product.code)) {
      selectedProducts.push(product);
      this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
    }
    this.catalogProductSearchTerm = '';
    this.showCatalogProductDropdown = false;
    this.filteredCatalogProducts = [];
  }

  addManualProductCode(): void {
    const code = (this.catalogProductSearchTerm || '').trim();
    if (!code) return;
    const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
    if (selectedProducts.find((p: any) => p.code === code)) {
      alertifyjs.warning(`El código "${code}" ya está en la lista.`);
      this.catalogProductSearchTerm = '';
      return;
    }
    selectedProducts.push({ code, name: '', brand: '', manual: true });
    this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
    this.catalogProductSearchTerm = '';
    this.showCatalogProductDropdown = false;
    this.filteredCatalogProducts = [];
  }

  onProductSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.filteredCatalogProducts.length > 0) {
        this.selectCatalogProduct(this.filteredCatalogProducts[0]);
      } else {
        this.addManualProductCode();
      }
    }
  }

  removeCatalogProduct(productToRemove: any): void {
    const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
    const index = selectedProducts.findIndex((p: any) => p.code === productToRemove.code);
    if (index > -1) {
      selectedProducts.splice(index, 1);
      this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
    }
  }

  removeAllCatalogProducts(): void {
    this.step6Form.get('productConfig.selectedProducts')?.setValue([]);
  }

  onExcelFileSelected(event: any): void {
    const file = event.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const selectedProducts = this.step6Form.get('productConfig.selectedProducts')?.value || [];
        let added = 0;
        let duplicates = 0;
        for (const row of rows) {
          const code = String(row[0] || '').trim();
          if (!code || code.toLowerCase() === 'codigo' || code.toLowerCase() === 'código' || code.toLowerCase() === 'code') continue;
          if (selectedProducts.find((p: any) => p.code === code)) {
            duplicates++;
            continue;
          }
          selectedProducts.push({ code, name: '', brand: '', manual: true });
          added++;
        }
        this.step6Form.get('productConfig.selectedProducts')?.setValue([...selectedProducts]);
        let msg = `Se agregaron ${added} producto(s).`;
        if (duplicates > 0) msg += ` ${duplicates} duplicado(s) omitido(s).`;
        alertifyjs.success(msg);
      } catch (err) {
        console.error('Error leyendo Excel:', err);
        alertifyjs.error('Error al leer el archivo Excel.');
      }
      event.target.value = '';
    };
    reader.readAsArrayBuffer(file);
  }

  hideCatalogProductDropdown(): void {
    setTimeout(() => {
      this.showCatalogProductDropdown = false;
    }, 150);
  }

  toggleProductFilters(enabled: boolean): void {
    this.step6Form.get('productConfig.enabled')?.setValue(enabled);
  }

  areProductFiltersEnabled(): boolean {
    return this.step6Form.get('productConfig.enabled')?.value || false;
  }

  /**
   * Verifica si el producto seleccionado es de seguros
   */
  isInsuranceProduct(): boolean {
    // Verificar si hay producto seleccionado con is_insurance_product
    if (this.selectedProduct && this.selectedProduct.insurance && this.selectedProduct.insurance.is_insurance_product) {
      return true;
    }

    // Verificar en addedProducts si existe al menos un producto de seguros
    if (this.addedProducts && this.addedProducts.length > 0) {
      return this.addedProducts.some((item: any) =>
        item.product && item.product.insurance && item.product.insurance.is_insurance_product
      );
    }

    return false;
  }

  /**
   * Actualiza las validaciones del campo prioridad según si es producto de seguros o no
   */
  private updatePriorityValidation(): void {
    const priorityControl = this.step4Form?.get('priority');
    if (!priorityControl) return;

    if (this.isInsuranceProduct()) {
      // Para productos de seguros: solo validar rango
      priorityControl.setValidators([Validators.min(0), Validators.max(999)]);
      console.log('✅ Prioridad configurada como OPCIONAL (producto de seguros)');
    } else {
      // Para otros productos: requerido + rango
      priorityControl.setValidators([Validators.required, Validators.min(0), Validators.max(999)]);
      console.log('✅ Prioridad configurada como REQUERIDA (producto normal)');
    }

    priorityControl.updateValueAndValidity();
  }

  // NUEVO: Método para debugging - obtener el JSON que se va a enviar
}
